import { add, Arcified, assignTHREEMatrix4FromMatrix4, Bend, BendInfoEngravePath, BoundingBox3, BoundingBoxNullable, BrakePunchDie, collinear, Contour, convexDecomposition, ConvexGroup, dot, extrudePolygon, getMatrix4FromTHREEMatrix4, getPointTangentOfEntity, intersectLineLine, isBboxDefined, isPointInsidePolys, max, min, mul, norm, norm2, normalize, ParsedBrakeDie, RawEntity, Region, sub, Vector } from '@oshcut/oshlib'
import { BoxGeometry, BufferGeometry, ExtrudeGeometry, Group, Line, LineBasicMaterial, Matrix4, Mesh, MeshBasicMaterial, MeshStandardMaterial, Path, Quaternion, Shape, Vector3 } from 'three'
import unitScale from './unitScale'
import { computePolyTopology } from './computePolyTopology'
import { getDefaultPunchDiePair, getPunchAndDie, machines } from './getBrakeTooling'
import { pointContourHitTest } from './partHitTest'

// TODO: If you change this to import, ClipperLib is an Object, which creates ts errors
const { ClipperLib } = require('@oshcut/oshlib')

const DIE_PART_PUNCH_MARGIN_INCH = 0.004

export type ToolingPosition = {
  bendId: number,
  lineGroupIndex: number,
  zStart: number,
  zEnd: number,
  color?: string,
}

export type ToolSetup = {
  punchDiePair: BrakePunchDie
  punchPositions: ToolingPosition[]
  diePositions: ToolingPosition[]
}

type ClipperPoint = { X: number, Y: number }

/**
 * Returns the punch/die pairs that are compatible with the material and bend angles of the given part, sorted by
 * priority. 
 * 
 * This special rule helps with the new Mits tooling: If the compatible pairs include multiple die types, only the
 * highest priority die type is included in the results. This will prevent including acute dies for 90 degree bends,
 * which are compatible with 90 degree bends yet have a lower priority than the 90 degree dies.
 * 
 * Note: This function appears in arcify, web_customer, oshcut_admin, and web_brake_operator, so that each app can use
 * the same logic when choosing punch die pairs. In arcify, it is used to select the correct die for bend sequencing. In
 * web_customer and oshcut_admin, it is used to present the user with a list of bend radius options. If you change this
 * function, you should also change it in those places.
 * 
 * @param arcified The part to get the punch/die pairs for.
 * @param machineId Only consider punch/die pairs for this machine. If not specified, all machines are considered.
 * @param dieId Only consider punch/die pairs with this die id. If not specified, all dies are considered.
 * @returns The compatible punch/die pairs, sorted by priority.
 */
export function getCompatiblePunchDiePairs(arcified: Arcified, machineId?: number, dieId?: number) {

  // Get the maximum bend angle in the part
  let maximumBendAngle = 0
  for (let contour of arcified.contours) {
    if (contour.cuttingMethod === 'CUTTING_METHOD_BEND' && contour.bendAngle) {
      maximumBendAngle = Math.max(maximumBendAngle, Math.abs(contour.bendAngle))
    }
  }

  // Get all compatible punch/die pairs
  let compatiblePairs = machines.flatMap(machine => machine.tooling.punchDiePairs.filter(pdp => {

    // Skip if the machine id is specified and this punch/die pair is not for that machine
    if (machineId != null && machine.machineId !== machineId) return false

    // Skip if the die id is specified and this punch/die pair is not for that die
    if (dieId != null && pdp.die !== dieId) return false

    // The machine tooling must be enabled
    if (!machine.tooling.enabled) return false

    // Must match material id (if specified)
    if (arcified.material && pdp.material_sheet_id !== arcified.material.id) return false

    // Must meet minimum bend angle requirement
    if (pdp.min_angle <= 180 - maximumBendAngle) return true

    return false
  }))

  compatiblePairs.sort((a, b) => a.priority - b.priority)

  // Special handling for when there are multiple compatible die "types", specifically the new Mits tooling. Examples of
  // "types" are:
  // - Wilson 86 degree dies ("Wilson-86")
  // - Wilson 30 degree (acute) dies ("Wilson-30")
  // - LVD 78 degree dies ("LVD-78")

  // If there are multiple compatible die "types", only keep pairs that match the highest priority die type.

  // For lack of a well-defined "type", we'll use manufacturer + angle.
  let { die: highestPriorityDie } = compatiblePairs[0] ? getPunchAndDie(compatiblePairs[0]) : { die: undefined }
  let highestPriorityDieType = `${highestPriorityDie?.manufacturer}-${highestPriorityDie?.angle}`

  // Filter out pairs that don't match the highest priority die type
  compatiblePairs = compatiblePairs.filter(pdp => {
    let machine = machines.find(m => m.machineId === pdp.machine_id)
    let die = machine?.tooling.dies.find(d => d.id === pdp.die)
    let dieType = `${die?.manufacturer}-${die?.angle}`
    return dieType === highestPriorityDieType
  })

  return compatiblePairs
}

/**
 * Gets the punch/die pairs for a given part, taking into account the pre-selected machine id, die id, material, and bend radius that are
 * stored in the part's bend info. If no punch/die pairs are found, the default punch/die pair is returned.
 */
export function getChosenPunchDiePairs(arcified: Arcified) {

  if (machines.length === 0) {
    throw new Error('tooling is not loaded yet!')
  }

  const defaults = [getDefaultPunchDiePair()]

  const bendInfo = arcified.bendInfo
  if (!bendInfo) return defaults

  const material = arcified.material
  if (!material) return defaults

  let machine = machines.find(t => t.machineId === bendInfo?.machineId)
  if (!machine) return defaults

  // Find all punch/die pairs that are compatible with the given material
  let punchDiePairs = getCompatiblePunchDiePairs(arcified, machine.machineId, arcified.bendInfo?.dieId)

  // Choose a die that is close to the bend radius we want. If automatic is chosen, we'll use the die with the
  // smallest radius.
  let punchDiePair: BrakePunchDie | undefined
  let minError = Infinity
  for (let pair of punchDiePairs) {
    let error = Math.abs(bendInfo.innerRadiusInch - pair.bend_radius_in)
    if (error < minError) {
      minError = error
      punchDiePair = pair
    }
  }

  if (!punchDiePair) return defaults

  // Return all punch/die pairs with the same die and bend_radius_in. This will be at least one pair (the one that
  // was found), but may be more.
  return machine.tooling.punchDiePairs
    .filter(pdp =>
    pdp.material_sheet_id === material.id
    && pdp.die === punchDiePair!.die
    && pdp.bend_radius_in === punchDiePair!.bend_radius_in
  )
    .sort((a, b) => a.priority - b.priority)

}

/**
 * Gets material thickness, k-factor, and part scale factor for a given arcified part, in part native units.
 * @param arcified The part to get material properties for.
 * @returns An object containing the material properties in part native units.
 */
export function getPartMaterialProperties(arcified: Arcified) {
  const T = arcified.material?.thickness || 0.0625
  const K = arcified.material?.k_factor

  const partScale = unitScale[arcified.units || 'in'] / (arcified.scaleFactor ?? 1)
  const thickness = T * partScale
  const kFactor = K ?? 0.5
  return { thickness, kFactor, partScale }
}

/**
 * Gets material thickness, inner bend radius, k-factor, part scale factor, and springback for a given arcified part and
 * punch/die pair, in part native units.
 * @param arcified The part to get material bend properties for.
 * @returns An object containing the material bend properties in part native units.
 */
export function getPartMaterialBendProperties(arcified: Arcified) {
  const R = arcified.bendInfo?.innerRadiusInch
  const E = arcified.material?.elastic_modulus
  const Y = arcified.material?.yield_tensile
  const T = arcified.material?.thickness || 0.0625
  const K = arcified.material?.k_factor

  const partScale = unitScale[arcified.units || 'in'] / (arcified.scaleFactor ?? 1)
  const thickness = T * partScale
  const innerRadius = R != null ? R * partScale : thickness
  const kFactor = K ?? 0.5
  let springback = 1
  if (R && E && Y) {
    const x = R * Y / (E * T)
    springback = 4 * Math.pow(x, 3) - 3 * x + 1
  }
  return { thickness, innerRadius, kFactor, partScale, springback }
}

/**
 * Creates a skeleton bendInfo object for a part without any bends, so that we can still make a 3D model from it.
 * @param arcified The part to create a skeleton bend info object for.
 * @returns The skeleton bend info object.
 */
function createSkeletonBendInfo(arcified: Arcified): Arcified['bendInfo'] {
  return {
    regions: arcified.contours
      .filter(c => c.type === 'outer') // One region for each outer contour (nested part)
      .map(c => ({
        id: c.id, // Region id will be the contour id
        polyGroup: [{ // Single outer poly
          outerPoly: c.finePoly,
          innerPolys: arcified.contours
            .filter(d => d.parentId === c.id && d.type === 'inner')
            .map(d => d.finePoly)
        }],
        bendIds: [],
        totalOuterArea: c.properties.area,
        originalOuterContourId: c.id

      })),
    bends: [],
    hasCycles: false,
    hasIncompleteBendLine: null,
    hasIntersectingBendLines: null,
    cannotExtendContour: null,
    machineId: 4,
    dieId: 19, // Default 24 mm die
    innerRadiusInch: 0.157
  }
}

/**
 * Adds convenience properties to bends, such as angle, allowance, tangent, normal, and minFlange.
 * @param arcified The part to add bend convenience properties to.
 */
export function createBendConvenienceProperties(arcified: Arcified, die: ParsedBrakeDie) {
  if (!arcified.bendInfo) return
  const { thickness, innerRadius, kFactor, partScale } = getPartMaterialBendProperties(arcified)

  for (let bend of arcified.bendInfo.bends) {
    let contour = arcified.contours.find(c => c.id === bend.originalContourIds[0])
    if (!contour) continue
    bend.angle = contour.bendAngle ?? 0
    bend.allowance = Math.abs(bend.angle) * Math.PI / 180 * (innerRadius + kFactor * thickness)
    /** Tangent of bend line, pointing from start to end */
    bend.tangent = normalize(sub(bend.lineGroup[0].end, bend.lineGroup[0].start))
    /** Normal of bend line, pointing toward the left side */
    bend.normal = { x: -bend.tangent.y, y: bend.tangent.x }

    if (die) {
      let vGapCircleCenter = Math.abs(die.v_gap_edge.center.x - die.profile_center_datum.x) * 2
      bend.minFlange = calculateMinimumFlangeLength(thickness, bend.angle * Math.PI / 180, innerRadius, kFactor, die.v_gap_edge.radius * partScale, vGapCircleCenter * partScale)
      // Add 0.1" plus half material thickness, on each side
      bend.minFlange += 0.05 * partScale
    } else {
      if (Math.abs(bend.angle) > 90) {
        bend.minFlange = (arcified.material?.bend_min_flange_gt90 || 0.412) * partScale
      } else {
        bend.minFlange = (arcified.material?.bend_min_flange_lte90 || 0.262) * partScale
      }
    }
  }
}

export function deleteBendConvenienceProperties(arcified: Arcified) {
  if (!arcified.bendInfo) return
  // TODO: It would be a whole lot nicer if instead of temporarily adding these properties to the bends, we just passed
  // them in as parameters to the functions that need them. That we wouldn't need to delete them, and they wouldn't
  // pollute the type.
  for (let bend of arcified.bendInfo.bends) {
    delete bend.angle
    delete bend.allowance
    delete bend.tangent
    delete bend.normal
    delete bend.parentSide
    delete bend.leftSide
    delete bend.rightSide
    delete bend.sideLineChild
    delete bend.sideLineParent
    delete bend.centerlineOfCurvature
    for (let line of bend.lineGroup) {
      // TODO: Some of these might still be necessary
      delete line.stripes
      delete line['baRegion-left']
      delete line['baRegion-right']
      delete line.baRegion
      delete line.mfRegion
    }
  }
}

/**
 * Gets the minimum tool width for a given machine in inches.
 * @param machineId The machine id.
 * @returns The minimum tool width in inches.
 */
export function getMinimumToolingWidth(machineId: number) {
  return machineId === 4 ? 0.99 : 0.99
}

export function createBendAllowanceRegions(arcified: Arcified) {

  if (!arcified.bendInfo) return
  const bendInfo = arcified.bendInfo
  const { partScale } = getPartMaterialProperties(arcified)

  /***** Find the bend allowance regions *****/
  // Each line in a bend will have a bend allowance region on each side, which is a rectangular area mapped onto the region.
  for (let region of bendInfo.regions) {
    // Log.info(region)
    for (let bendId of region.bendIds) {
      const bend = bendInfo.bends.find(b => b.id === bendId)
      if (!bend) throw new Error(`Could not find bend ${bendId} in bendInfo.bends`)
      if (bend.tangent == null || bend.normal == null || bend.minFlange == null) {
        throw new Error(`Bend ${bendId} is missing convenience properties`)
      }
      // Log.info(bend)
      // Determine what side of the bend this region is on. We only compute one side of each bend at a time.
      let side: 'left' | 'right'
      if (bend.regionIdLeft === region.id) {
        side = 'left'
      } else if (bend.regionIdRight === region.id) {
        side = 'right'
      } else {
        throw new Error('Could not determine which side of the bend this region is on')
      }

      // Log.info(`Finding bend allowance region for region ${region.id} on the ${side} side of bend ${bend.id}, which contains ${bend.lineGroup.length} lines`)

      for (let line of bend.lineGroup) {

        // Log.info(line)

        // Find the starting polys and indices for this line where we will start traversing
        let { bestPolyStart, bestPolyIdxStart, bestPolyEnd, bestPolyIdxEnd } = findMatchingPolysAndIndexes(region.polyGroup, line)


        // Now traverse forward and backward from bestIdxStart and bestIdxEnd to build the baRegion for this line on this side
        // Draw an imaginary box around the bend line that is bend.allowance/2 distant in each direction orthogonal to the bend line
        // Traverse until we are outside that box

        let baRegion = traverseBendAllowanceRegion(bend, bestPolyStart, bestPolyIdxStart, side, 'start')
        if (baRegion.length < bestPolyStart.length) {
          // If we did not traverse around bestPolyStart completely, then also traverse around bestPolyEnd and concatenate the two
          baRegion.push(...traverseBendAllowanceRegion(bend, bestPolyEnd, bestPolyIdxEnd, side, 'end').reverse())
        }
        if (side === 'left') {
          baRegion.reverse()
        }

        // Log.info('The baRegion is:')
        // Log.info(baRegion)

        let key = `baRegion-${side}` as const
        // if (line.hasOwnProperty(key)) {
        //   throw new Error('Trying to set second baRegion for same side of a line')
        // }
        line[key] = baRegion

        // Add minimum flange region
        let minDieLengthInch = getMinimumToolingWidth(bendInfo.machineId)
        let adjustedLine = adjustPunchline(line, {
          dieShorten: 0.125 * partScale,
          minDieLengthPct: 75,
          minDieLength: minDieLengthInch * partScale
        })

        line.mfRegion = [
          add(adjustedLine.start, mul(bend.normal, bend.minFlange)),
          add(adjustedLine.start, mul(bend.normal, -bend.minFlange)),
          add(adjustedLine.end, mul(bend.normal, -bend.minFlange)),
          add(adjustedLine.end, mul(bend.normal, bend.minFlange)),
        ]
      }
    }
  }

  for (let bend of bendInfo.bends) {
    for (let line of bend.lineGroup) {
      if (!line['baRegion-left']) throw new Error(`Missing baRegion-left for bend ${bend.id}`)
      if (!line['baRegion-right']) throw new Error(`Missing baRegion-right for bend ${bend.id}`)

      // Join left and right bend allowance regions
      // Because of how we traversed them, we can simply concat them to join them into a single region
      line.baRegion = line['baRegion-left'].concat(line['baRegion-right'])
    }
  }
}

/**
 * Find the points on a polyGroup that intersect a bend line. The polyGroup must belong to a region adjacent to the bend
 * line.
 * 
 * Different polys for the start and endpoints might be returned, if one of the points is on an outer contour and the
 * other is on an inner contour, for example.
 * 
 * Using this information, the caller could construct a bend allowance region that includes the bend line, follows the contours
 * 
 * @param polyGroup The polyGroup belonging to the region
 * @param line The bend line
 * @returns The polys and vertex indicies in those polys that match the endpoints of the line
 */
function findMatchingPolysAndIndexes(polyGroup: Region['polyGroup'], line: Bend['lineGroup'][0]): {
  bestPolyStart: Vector[];
  bestPolyIdxStart: number;
  bestPolyEnd: Vector[];
  bestPolyIdxEnd: number;
} {


  // Match the start point
  let bestPolyStart: Vector[] | undefined
  let bestPolyIdxStart: number | undefined
  let bestD2Start = Infinity

  // Try all outer polys first, then the inner polys
  for (let j = 0; j < polyGroup.length; j++) {
    for (let k = 0; k < polyGroup[j].outerPoly.length; k++) {
      let d2 = norm2(sub(polyGroup[j].outerPoly[k], line.start))
      if (d2 < bestD2Start) {
        bestD2Start = d2
        bestPolyStart = polyGroup[j].outerPoly
        bestPolyIdxStart = k
        if (bestD2Start < 1e-24) break // Good enough, quit searching early
      }
    }
  }

  if (bestD2Start > 1e-6) {
    // Not good enough; try inner polys
    for (let j = 0; j < polyGroup.length; j++) {
      for (let j2 = 0; j2 < polyGroup[j].innerPolys.length; j2++) {
        for (let k = 0; k < polyGroup[j].innerPolys[j2].length; k++) {
          let d2 = norm2(sub(polyGroup[j].innerPolys[j2][k], line.start))
          if (d2 < bestD2Start) {
            bestD2Start = d2
            bestPolyStart = polyGroup[j].innerPolys[j2]
            bestPolyIdxStart = k
            if (bestD2Start < 1e-24) break // Good enough, quit searching early
          }
        }
      }
    }
  }
  if (bestPolyIdxStart == null || bestPolyStart == null) throw new Error('Could not find poly for bend line start')

  // Match the end point
  let bestPolyEnd: Vector[] | undefined
  let bestPolyIdxEnd: number | undefined
  let bestD2End = Infinity

  // Try some educated guesses before doing an exhaustive search. If the other side of the bend is the same poly, then
  // the other index should be adjacent, depending on which side it is.

  // Assumption: the line intersects the same polyGroup on this end as on the other end
  bestPolyEnd = bestPolyStart

  // If the assumption is correct and the side is "left", then the end index should be:
  bestPolyIdxEnd = (bestPolyIdxStart + 1) % bestPolyStart.length
  bestD2End = norm2(sub(bestPolyEnd[bestPolyIdxEnd], line.end))

  if (bestD2End > 1e-6) {
    // That wasn't it.
    // If the assumption is correct and the side is "right", then the end index should be:
    bestPolyIdxEnd = (bestPolyIdxStart - 1 + bestPolyStart.length) % bestPolyStart.length
    bestD2End = norm2(sub(bestPolyEnd[bestPolyIdxEnd], line.end))
  }

  if (bestD2End > 1e-6) {
    // The assumption was wrong. Try all outer polys first, then the inner polys.
    for (let j = 0; j < polyGroup.length; j++) {
      for (let k = 0; k < polyGroup[j].outerPoly.length; k++) {
        let d2 = norm2(sub(polyGroup[j].outerPoly[k], line.end))
        if (d2 < bestD2End) {
          bestD2End = d2
          bestPolyEnd = polyGroup[j].outerPoly
          bestPolyIdxEnd = k
          if (bestD2End < 1e-24) break // Good enough, quit searching early
        }
      }
    }
  }

  if (bestD2End > 1e-6) {
    // Not good enough; try inner polys
    for (let j = 0; j < polyGroup.length; j++) {
      for (let j2 = 0; j2 < polyGroup[j].innerPolys.length; j2++) {
        for (let k = 0; k < polyGroup[j].innerPolys[j].length; k++) {
          let d2 = norm2(sub(polyGroup[j].innerPolys[j][k], line.end))
          if (d2 < bestD2End) {
            bestD2End = d2
            bestPolyEnd = polyGroup[j].innerPolys[j2]
            bestPolyIdxEnd = k
            if (bestD2End < 1e-24) break // Good enough, quit searching early
          }
        }
      }
    }
  }

  if (bestD2Start > 1e-6 || bestD2End > 1e-6) {
    throw new Error('Could not find vertex on polyGroup matching bend line endpoint')
  }

  return { bestPolyStart, bestPolyIdxStart, bestPolyEnd, bestPolyIdxEnd }

}

/**
 * Traverse one side of a bend allowance region starting at a given poly index, and return the points traversed. The
 * traversal is bounded by the bend line, the poly, and an imaginary rectangle that extends outward from the bend line
 * by the bend allowance.
 * 
 * The traversal does not always complete a full loop around the poly. It can terminate early if it leaves the bend
 * allowance rectangle. In that case, it is standard procedure to call this function again, beginning from the other
 * end of the bend line, then concatenate the two results (with the second result reversed).
 * @param bend The bend line whose allowance region we are traversing.
 * @param poly The poly that bounds the allowance region.
 * @param startIndex The index of the poly that is closest to the bend line start (or end).
 * @param side The side of the bend that we are traversing.
 * @param end Which end of the bend line we starting from.
 * @returns The points traversed.
 */
function traverseBendAllowanceRegion(bend: Bend, poly: Vector[], startIndex: number, side: 'left' | 'right', end: 'start' | 'end') {

  if (bend.normal == null || bend.tangent == null || bend.allowance == null) {
    throw new Error('Bend is missing normal, tangent, or allowance')
  }

  const thresholdDistTangFromBendLine = 1

  let traversed = []

  // Log.info(`Traversing side ${side}, ${end}. j=${j}, k=${k}`)

  let k = startIndex

  let ptStart = poly[k]
  let ptLast = ptStart
  traversed.push(ptStart)
  while (true) {
    // If we are on the left side, we will traverse the poly forward from end and backward from start
    // If we are on the right side, we will traverse the poly forward from start and backward from end
    if ((end === 'start' && side === 'right') || (end === 'end' && side === 'left')) {
      k = (k + 1) % poly.length
    } else {
      k = (k - 1 + poly.length) % poly.length
    }
    // Log.info(`k=${k}`)
    if (traversed.length === poly.length) {
      // We've added the entire poly without ever leaving the bend allowance region
      // Log.info('We have traversed the entire poly')
      break
    }
    let pt = poly[k]

    let distNormFromBendLine = dot(bend.normal, sub(pt, ptStart)) * (side === 'left' ? 1 : -1) / (bend.allowance * 0.5)
    let distTangFromBendLine = dot(bend.tangent, sub(pt, ptStart)) * (end === 'end' ? 1 : -1) / (bend.allowance * 0.5) / thresholdDistTangFromBendLine

    if (distNormFromBendLine > 1 || distTangFromBendLine > 1) {
      // Gone too far. Interpolate the point right here, then stop traversing

      let currTNorm = distNormFromBendLine
      let lastTNorm = dot(bend.normal, sub(ptLast, ptStart)) * (side === 'left' ? 1 : -1) / (bend.allowance * 0.5)
      let currTTang = distTangFromBendLine
      let lastTTang = dot(bend.tangent, sub(ptLast, ptStart)) * (end === 'end' ? 1 : -1) / (bend.allowance * 0.5) / thresholdDistTangFromBendLine

      let tNorm = (1 - lastTNorm) / (currTNorm - lastTNorm)
      let tTang = (1 - lastTTang) / (currTTang - lastTTang)
      if (Number.isNaN(tNorm) || !Number.isFinite(tNorm)) tNorm = 0
      if (Number.isNaN(tTang) || !Number.isFinite(tTang)) tTang = 0

      let t, needsFill = false
      if (distNormFromBendLine > 1 && distTangFromBendLine > 1) {
        t = Math.min(tNorm, tTang)
        if (tNorm > tTang) {
          needsFill = true
        }
      }
      else if (distNormFromBendLine > 1) {
        t = tNorm
      }
      else if (distTangFromBendLine > 1) {
        t = tTang
        needsFill = true
      } else {
        throw new Error('Should not be here')
      }

      pt = add(mul(ptLast, 1 - t), mul(pt, t))

      traversed.push(pt)
      if (needsFill) {
        // Log.info('We have gone to far in the tangent direction, so fill out the region in the normal direction so the edge of the bend region is parallel to the bend line')
        let fillPt = add(
          add(
            ptStart,
            mul(bend.normal, bend.allowance * 0.5 * (side === 'left' ? 1 : -1))
          ),
          mul(bend.tangent, bend.allowance * 0.5 * (end === 'end' ? 1 : -1) * thresholdDistTangFromBendLine)
        )
        traversed.push(fillPt)
      }
      break
    } else if (distNormFromBendLine < -1) {
      // We've gone completely the wrong direction!
      // Just fill in one side of the box
      let fillPt1 = add(
        ptStart,
        mul(bend.tangent, bend.allowance * 0.5 * (end === 'end' ? 1 : -1) * thresholdDistTangFromBendLine)
      )
      let fillPt2 = add(
        fillPt1,
        mul(bend.normal, bend.allowance * 0.5 * (side === 'left' ? 1 : -1))
      )
      traversed = [ptStart, fillPt1, fillPt2]
      break
    } else {
      traversed.push(pt)
    }
    ptLast = pt
  }

  return traversed
}

export function deleteBendAllowanceRegions(arcified: Arcified) {
  if (!arcified.bendInfo) return
  for (let bend of arcified.bendInfo.bends) {
    for (let line of bend.lineGroup) {
      delete line['baRegion-left']
      delete line['baRegion-right']
      delete line['baRegion']
      delete line['mfRegion']
    }
  }
}


function trimBendAllowanceFromRegions(arcified: Arcified) {
  let bendInfo = arcified.bendInfo
  if (!bendInfo) return
  for (let region of bendInfo.regions) {

    let clipperRemoveBendRegions = new ClipperLib.Clipper()
    let solutionRemoveBendRegions: ClipperPoint[][] = new ClipperLib.Paths()

    // First add all of this region's polys
    for (let poly of region.polyGroup) {
      clipperRemoveBendRegions.AddPath(poly.outerPoly.map(toClipper), ClipperLib.PolyType.ptSubject, true)
      clipperRemoveBendRegions.AddPaths(poly.innerPolys.map(ip => ip.map(toClipper)), ClipperLib.PolyType.ptSubject, true)
    }

    for (let bendId of region.bendIds) {
      const bend = bendInfo.bends.find(b => b.id === bendId)
      if (!bend) throw new Error('Could not find bend')
      const bendNormal = bend.normal
      if (!bendNormal) throw new Error('Bend has no normal')
      // Log.info(bend)
      // Determine which side of the bend this region is on

      // Determine which side of the bend this region is on
      let side: 'left' | 'right'
      if (bend.regionIdLeft === region.id) {
        side = 'left'
      } else if (bend.regionIdRight === region.id) {
        side = 'right'
      } else {
        throw new Error('Could not determine which side of the bend this region is on')
      }

      // Log.info(`Trimming bend allowance regions for region ${region.id} on the ${side} side of bend ${bend.id}, which contains ${bend.lineGroup.length} lines`)

      for (let line of bend.lineGroup) {
        // Subtract this line's bend allowance region 
        let key = `baRegion-${side}` as const
        let baRegion = line[key]
        if (!baRegion) throw new Error('Could not find bend allowance region ' + key + ' for bend ' + bend.id)
        // TODO: Enlarge the baRegion in the direction of the bend line so that it comfortably removes ALL of the outerPoly (otherwise, a thin sliver sometimes remains) 

        // The scaling transform is the matrix whose eigenvectors are the normalized bend line and its perpendicular bisector, and the eigenvalues are the scaling factor and 1, respectively
        let v = normalize(sub(line.end, line.start))
        let lambda1 = 1.0001
        let lambda2 = 1

        // Skipping ahead (work done on paper)
        let A = [
          [v.x * v.x * lambda1 + v.y * v.y * lambda2, v.x * v.y * (lambda1 - lambda2)],
          [v.x * v.y * (lambda1 - lambda2), v.y * v.y * lambda1 + v.x * v.x * lambda2]
        ]

        let lineCtr = mul(add(line.start, line.end), 0.5)
        let enlarged = baRegion.map(pt => {
          pt = sub(pt, lineCtr)
          pt = {
            x: A[0][0] * pt.x + A[0][1] * pt.y,
            y: A[1][0] * pt.x + A[1][1] * pt.y
          }
          pt = add(pt, lineCtr)
          // Move region slightly toward bend line, so that we don't have a sliver left on that side either
          pt = add(pt, mul(bendNormal, (side === 'right' ? 1 : -1) * 0.00001))
          return pt
        })

        clipperRemoveBendRegions.AddPath(enlarged.map(toClipper), ClipperLib.PolyType.ptClip, true)
      }
    }


    // Execute
    // (Use pftNonZero so that overlapping bend allowance regions do not "cancel" each other out and leave artifacts)
    clipperRemoveBendRegions.Execute(ClipperLib.ClipType.ctDifference, solutionRemoveBendRegions, ClipperLib.PolyFillType.pftEvenOdd, ClipperLib.PolyFillType.pftNonZero)

    // This could contain multiple paths, and even multiple outer paths. It will not contain nested paths.
    region.polyTrimmed = solutionRemoveBendRegions.map(path => path.map(fromClipper))
    region.polyTrimmedTopos = computePolyTopology(region.polyTrimmed)

    // Log.info(`After trimming entire region ${region.id}, polyTrimmed = `)
    // Log.info(region.polyTrimmed)
    // Log.info(`polyTrimmedTopos = `)
    // Log.info(region.polyTrimmedTopos)
  }
}

function createBendStrips(arcified: Arcified) {
  let bendInfo = arcified.bendInfo
  if (!bendInfo) return
  for (let bend of bendInfo.bends) {

    if (bend.angle == null || bend.normal == null || bend.allowance == null || bend.tangent == null) {
      throw new Error('Bend is missing convenience properties')
    }

    // Use increments of 15 degrees, say? 
    let increments = Math.ceil(Math.abs(bend.angle) / 15)

    // Log.info(`Create strips for lines in bend ${bend.id}`)
    for (let line of bend.lineGroup) {

      if (!line.baRegion) {
        throw new Error('line does not contain a baRegion')
      }


      // Chop the baRegion into strips

      line.stripes = []
      for (let i = 0; i < increments; i++) {
        let clipperStripe = new ClipperLib.Clipper()
        let solutionStripe: ClipperPoint[][] = new ClipperLib.Paths()

        // Add baRegion to the clipper as a subject
        clipperStripe.AddPath(line.baRegion.map(toClipper), ClipperLib.PolyType.ptSubject, true)

        // TODO: The normal and/or tangent might be in the wrong direction
        let stripeRegion = [
          add(add(line.start, mul(bend.normal, (-bend.allowance * 0.5 + i * bend.allowance / increments))), mul(bend.tangent, -bend.allowance * 0.5)),
          add(add(line.start, mul(bend.normal, (-bend.allowance * 0.5 + (i + 1) * bend.allowance / increments))), mul(bend.tangent, -bend.allowance * 0.5)),
          add(add(line.end, mul(bend.normal, (-bend.allowance * 0.5 + (i + 1) * bend.allowance / increments))), mul(bend.tangent, bend.allowance * 0.5)),
          add(add(line.end, mul(bend.normal, (-bend.allowance * 0.5 + i * bend.allowance / increments))), mul(bend.tangent, bend.allowance * 0.5)),
        ]

        // Log.info('This stripe region is ')
        // Log.info(stripeRegion)

        // Add the stripe to the clipper as a clip
        clipperStripe.AddPath(stripeRegion.map(toClipper), ClipperLib.PolyType.ptClip, true)
        clipperStripe.Execute(ClipperLib.ClipType.ctIntersection, solutionStripe)

        // solutionStripe could have multiple paths. If it contains holes, they are likely very small (less than the width of one stripe), and can be ignored.
        line.stripes.push(solutionStripe.map(soln => soln.map(fromClipper)))

      }
      // Log.info('The strips are: ')
      // Log.info(line.stripes)
    }
  }
}

export function createBendModel(
  arcified: Arcified,
  die: ParsedBrakeDie,
  {
    colorCodeBends = false,
    wireframe = false,
    disableBendTransforms = false,
    selectedContours = [],
    minHoleSize = 0,
    minHoleSizeCollision = 0,
    colorTopBottom = false,
    renderEngraveLines = false,
    percentBendTransform = 100,
    percentBendTransformById = null,
    finishColor = null,
    finishRoughness = 0.5
  }: {
    colorCodeBends?: boolean,
    wireframe?: boolean,
    disableBendTransforms?: boolean,
    selectedContours?: { contourId: number }[],
    minHoleSize?: number,
    minHoleSizeCollision?: number,
    colorTopBottom?: boolean,
    renderEngraveLines?: boolean,
    percentBendTransform?: number,
    percentBendTransformById?: Record<number, number> | null,
    finishColor?: string | null,
    finishRoughness?: number
  } = {}) {

  if (!arcified.bendInfo && arcified.contours.some(c => c.cuttingMethod === 'CUTTING_METHOD_BEND')) {
    throw new Error('Part contains bends but no bendInfo')
  }

  let model = new Group()

  const { thickness, innerRadius } = getPartMaterialBendProperties(arcified)

  // If the part does not have bendInfo, and contains no bends, create a skeleton bendInfo object so we can still render it
  if (!arcified.bendInfo && !arcified.contours.some(c => c.cuttingMethod === 'CUTTING_METHOD_BEND')) {
    // Add temporary bendInfo without mutating the original arcified object
    arcified = {
      ...arcified,
      bendInfo: createSkeletonBendInfo(arcified)
    }
  }

  const bendInfo = arcified.bendInfo

  if (!bendInfo) {
    throw new Error('arcified part does not have bendInfo')
  }


  // New procedure:
  // For each region:
  //   DONE Traverse the polyGroup forward or backward from the endpoint of each line of the lineGroup to create the bend allowance regions
  //   Use clipper to subtract the bend allowance region around each line of the lineGroup in each bend.
  //   Subtract or union the bend allowance region with the polyGroup in order to create the strips and to create the trimmed region
  //   Store the trimmed region in the region as a new polyTrimmed property.

  // For each region, while performing the traversing above, store the strips used to render the bend allowance region cylindrically, for each of its bends. Store this in each bend (as referenced by the region) under properties named stripesLeft or stripesRight.

  // Beginning at the root region of each connected region (regions are sorted by size), traverse across each bend, and then through regions, adding transforms to a "bend stack" and assigning the bend stack to each region and each bend in turn. Do this using DFS, and continue to other untraversed trees (those that do not yet have bend stacks), following the regions array in order so that large areas are the ones that are rooted first.


  // Add some additional properties to each bend for convenience
  createBendConvenienceProperties(arcified, die)

  /***** Create bend allowance regions *****/
  createBendAllowanceRegions(arcified)

  /***** Trim bend allowance region from the polys *****/
  trimBendAllowanceFromRegions(arcified)

  /***** Create bend strips for each line *****/
  createBendStrips(arcified)

  // For each contour and for each bend, create an array which contains all previous bends in order from the root contour so that we can choose any contour or bend at random and determine its position in space.


  // TODO: Separate out as much of the rotation and transformation as possible, which would allow us to alter the transforms of a model without recreating any of the model.
  // So we create the models first, naming them by convention, then we come back later to apply our chosen transforms.
  // For each bend, we'll be able to select any value between 0 and 100% of that bend's angle to use as the transform. But since we aren't repeating the model creation, this means that the cylindrical transforms of the bend will be wrong. But, this will be okay.




  /** Starting from contour, assign its bendStack (and that of all co-planar contours) and then traverse unvisited bends (bends without a bendStack) */
  const traverseRegion = (region: Region, bendStack: number[]) => {
    // Log.info(`traverseRegion(${region.id})`)
    region.bendStack = bendStack


    for (let bendId of region.bendIds) {
      let bend = bendInfo.bends.find(b => b.id === bendId)
      if (!bend) throw new Error(`Could not find bend ${bendId}`)
      bend.continent = region.continent
      // Traverse this bend
      if (!bend.bendStack) {
        traverseBend(bend, bendStack, region)
      }
    }
  }


  /** Starting from bend, assign its bendStack and then traverse unvisited contours (contours without a bendStack) */
  let traverseFail = false
  const traverseBend = (bend: Bend, bendStack: number[], previousRegion: Region) => {
    // Log.info(`traverseBend(${bend.id})`)
    bend.bendStack = bendStack

    // Determine which side of this bend the "parent" contour is on. This is needed to determine the centerline of curvature of the bend.

    let nextRegion
    if (bend.regionIdLeft === previousRegion.id) {
      bend.parentSide = 'left'
      nextRegion = bendInfo.regions.find(r => r.id === bend.regionIdRight)
      if (!nextRegion) throw new Error(`Could not find region ${bend.regionIdRight}`)
    } else if (bend.regionIdRight === previousRegion.id) {
      bend.parentSide = 'right'
      nextRegion = bendInfo.regions.find(r => r.id === bend.regionIdLeft)
      if (!nextRegion) throw new Error(`Could not find region ${bend.regionIdLeft}`)
    } else {
      throw new Error('Could not determine which side of the bend is the parent contour')
    }
    nextRegion.continent = bend.continent
    traverseRegion(nextRegion, bendStack.concat(bend.id))

  }

  for (let region of bendInfo.regions) {
    delete region.bendStack
  }
  for (let bend of bendInfo.bends) {
    delete bend.bendStack
  }

  // Regions are already ordered largest to smallest. Begin traversing from the largest. Continue picking new regions until no unvisted regions remain.
  let continent = 0

  if (arcified.unfold?.baseFacePoint && bendInfo.regions.length > 0) {
    // Choose first region based on base face point

    let partForHitTesting = {
      // bbox: is not needed
      contours: bendInfo.regions.map(region => {
        let entities: RawEntity[] = []

        for (let polyGroup of region.polyGroup) {
          for (let j = 0; j < polyGroup.outerPoly.length; j++) {
            entities.push({
              type: 'LINE',
              start: polyGroup.outerPoly[j],
              end: polyGroup.outerPoly[(j + 1) % polyGroup.outerPoly.length],
            })
          }
          for (let innerPoly of polyGroup.innerPolys) {
            for (let j = 0; j < innerPoly.length; j++) {
              entities.push({
                type: 'LINE',
                start: innerPoly[j],
                end: innerPoly[(j + 1) % innerPoly.length],
              })
            }
          }
        }

        // for (let i = 0; i < region.polyGroup.length; i++) {
        //   for (let j = 0; j < region.polyGroup[i].outerPoly.length; j++) {
        //     // Inner polys are not needed. Hit test will return the nearest outer poly, which should be the parent poly.
        //     entities.push({
        //       type: 'LINE',
        //       start: region.polyGroup[i].outerPoly[j],
        //       end: region.polyGroup[i].outerPoly[(j + 1) % region.polyGroup[i].outerPoly.length],
        //     })
        //   }
        // }
        return {
          id: region.id,
          // bbox: is not needed
          entities
        }
      })
    }

    // Apply offset, mirror, and rotation to the base face point
    let baseFacePoint = { ...arcified.unfold.baseFacePoint }
    baseFacePoint.x += arcified.offset?.x ?? 0
    baseFacePoint.y += arcified.offset?.y ?? 0
    if (arcified.optimizeRotationResult?.angle) {
      const cos = Math.cos(arcified.optimizeRotationResult.angle)
      const sin = Math.sin(arcified.optimizeRotationResult.angle)
      baseFacePoint = {
        x: baseFacePoint.x * cos - baseFacePoint.y * sin,
        y: baseFacePoint.x * sin + baseFacePoint.y * cos,
        z: baseFacePoint.z
      }
    }
    if (arcified.mirrorOptionResult?.mirrorApplied) {
      baseFacePoint = {
        x: -baseFacePoint.x,
        y: baseFacePoint.y,
        z: baseFacePoint.z
      }
    }

    const hitTestResult: any = pointContourHitTest(baseFacePoint, partForHitTesting)
    if (hitTestResult?.contour) {
      let firstRegion = bendInfo.regions.find(region => region.id === hitTestResult.contour!.id) // contour was defined just above this
      if (firstRegion) {
        firstRegion.continent = continent
        traverseRegion(firstRegion, [])
        continent++
      }
    }
  }

  // Traverse all remaining regions
  for (let region of bendInfo.regions) {
    if (!region.bendStack) {
      region.continent = continent
      traverseRegion(region, [])
      continent++
    }
  }

  if (traverseFail) return

  // No need to set the bendStack of holes

  // Set leftSide, rightSide, sideLineParent, sideLineChild, and centerLineOfCurvature for each bend
  for (let bend of bendInfo.bends) {
    if (!bend.normal) throw new Error('bend.normal is undefined')
    if (bend.allowance == null) throw new Error('bend.allowance is undefined')
    if (bend.angle == null) throw new Error('bend.angle is undefined')

    // Since the transform is the same no matter which line is used, we can just use the first.
    bend.rightSide = {
      start: add(bend.lineGroup[0].start, mul(bend.normal, -bend.allowance * 0.5)),
      end: add(bend.lineGroup[0].end, mul(bend.normal, -bend.allowance * 0.5))
    }
    bend.leftSide = {
      start: add(bend.lineGroup[0].start, mul(bend.normal, bend.allowance * 0.5)),
      end: add(bend.lineGroup[0].end, mul(bend.normal, bend.allowance * 0.5))
    }

    if (bend.parentSide === 'left') {
      bend.sideLineParent = { start: { ...bend.leftSide.start }, end: { ...bend.leftSide.end } }
      bend.sideLineChild = { start: { ...bend.rightSide.start }, end: { ...bend.rightSide.end } }
    } else if (bend.parentSide === 'right') {
      bend.sideLineParent = { start: { ...bend.rightSide.start }, end: { ...bend.rightSide.end } }
      bend.sideLineChild = { start: { ...bend.leftSide.start }, end: { ...bend.leftSide.end } }
    } else {
      throw new Error('Not all bends were traversed')
    }

    let centerlineOfCurvature = { start: { ...bend.sideLineParent.start, z: 0 }, end: { ...bend.sideLineParent.end, z: 0 } }
    if (bend.angle > 0) {
      centerlineOfCurvature.start.z = innerRadius + thickness
      centerlineOfCurvature.end.z = innerRadius + thickness
    } else {
      centerlineOfCurvature.start.z = -innerRadius
      centerlineOfCurvature.end.z = -innerRadius
    }
    bend.centerlineOfCurvature = centerlineOfCurvature
  }

  let engravePaths: BendInfoEngravePath[] = []

  if (renderEngraveLines) {
    // Combine all region polys and bend stripes into one array
    // Create new array by shallow cloning engrave lines (we are not mutating coordinates, just the order and items in the array)
    // Intersect all polys with all engrave lines
    // At each intersection, divide the engrave line into two pieces (just treat all engrave lines as open, for simplicity)
    // After all intersections have been completed, use isPointInsidePolys and assign each engrave line to a region or bend stripe.
    // During rendering, use reference equality on the engravePath's parent property with each region and bend stripe that is rendered, to render the engrave line using the appropriate transform.

    let engravePathsSimple = arcified.contours
      .filter(c => c.cuttingMethod === 'CUTTING_METHOD_ENGRAVE')
      .map(c => c.finePoly.slice()) // Shallow clone because we are going to mutate these arrays

    // Add each region's trimmed polys to allPolys
    let allPolys = bendInfo.regions.flatMap(r => {
      if (!r.polyTrimmed) throw new Error(`region ${r.id} has no polyTrimmed`)
      return r.polyTrimmed
    })

    // Add bend stripes to allPolys
    allPolys.push(...bendInfo.bends.flatMap(bend => bend.lineGroup.flatMap(line => {
      if (!line.stripes) throw new Error(`bend ${bend.id} has no stripes`)
      return line.stripes.flat()
    })))

    // Split engrave paths at edge of each poly
    let abortEngravePathSplitting = false
    for (let poly of allPolys) {
      for (let i = 0; i < poly.length; i++) {
        let polySegment = {
          start: poly[i],
          end: poly[(i + 1) % poly.length]
        }

        for (let j = 0; j < engravePathsSimple.length; j++) {
          let ePath = engravePathsSimple[j]
          for (let k = 0; k < ePath.length - 1; k++) {
            let engraveSegment = {
              start: ePath[k],
              end: ePath[k + 1]
            }

            // If polySegment and engraveSegment or colinear, it could cause an infinite loop, and we don't need to split it anyway.
            if (collinear(polySegment, engraveSegment, 1e-6)) {
              continue
            }

            let isects = intersectLineLine(polySegment, engraveSegment)
            if (isects.length > 0) {

              // Make sure we don't match the same point again
              let d2 = norm2(sub(engraveSegment.start, isects[0]))
              if (d2 > 1e-18) {

                // The engrave line intersects the poly 
                // Create two new paths
                let newEPath1 = [...ePath.slice(0, k + 1), isects[0]]
                let newEPath2 = [isects[0], ...ePath.slice(k + 1)]
                // No need to traverse newEPath1 again, so replace engravePaths[j] with it. Then insert newEPath2 at index j+1.
                engravePathsSimple[j] = newEPath1
                engravePathsSimple.splice(j + 1, 0, newEPath2)
                // Stop traversing ePath, move to index j+1, which is newEPath2
                break
              }
            }
          }
          if (engravePathsSimple.length > 100000) {
            // Prevent infinite loop
            console.warn('Too many engrave paths. Aborting engrave path splitting.')
            abortEngravePathSplitting = true
            engravePathsSimple = []
            break
          }
        }
        if (abortEngravePathSplitting) break
      }
      if (abortEngravePathSplitting) break
    }

    engravePaths = engravePathsSimple.map(ePath => ({ poly: ePath, parent: null }))

    // Hit test each engrave path against each region's polys
    for (let region of bendInfo.regions) {
      let polys = region.polyTrimmed
      if (!polys) throw new Error(`region ${region.id} has no polyTrimmed`)
      for (let ePath of engravePaths) {
        let pt = mul(add(ePath.poly[0], ePath.poly[1]), 0.5)
        if (isPointInsidePolys(pt, polys, false)) {
          ePath.parent = region
        }
      }
    }

    // Hit test each engrave path against each bend stripe
    for (let bend of bendInfo.bends) {
      for (let line of bend.lineGroup) {
        if (!line.stripes) {
          throw new Error(`bend ${bend.id} has no stripes`)
        }
        for (let stripe of line.stripes) {
          let polys = stripe
          for (let ePath of engravePaths) {
            let pt = mul(add(ePath.poly[0], ePath.poly[1]), 0.5)
            if (isPointInsidePolys(pt, polys, false)) {
              ePath.parent = bend
            }
          }
        }
      }
    }
  }

  // Discard any engrave paths that do not lie within the part
  engravePaths = engravePaths.filter(ePath => ePath.parent != null)

  bendInfo.engravePaths = engravePaths


  let extrudeSettings = {
    steps: 1,
    depth: thickness,
    bevelEnabled: false
  }

  // Create the flat contours
  let colors, backColors

  if (colorCodeBends) {
    colors = [
      '#1b9e77', // 0 Teal
      '#d95f02', // 1 Orange
      '#7570b3', // 2 Blue/Purple
      '#e7298a', // 3 Magenta
      '#66a61e', // 4 Green
      '#e6ab02', // 5 Yellow
      '#a6761d', // 6 Brown
      '#666666', // 7 Gray
      '#8dd3c7', // 8 Cyan
      '#ffffb3',
      '#bebada',
      '#fb8072',
      '#80b1d3',
      '#fdb462',
      '#b3de69',
      '#fccde5',
    ]
    backColors = colors
  } else {
    // colors = [color]
    colors = ['#90b8da']
    backColors = ['#90daa7']
  }

  for (let region of bendInfo.regions) {

    if (!region.polyTrimmed) throw new Error(`region ${region.id} has no polyTrimmed`)
    if (!region.polyTrimmedTopos) throw new Error(`region ${region.id} has no polyTrimmedTopos`)

    // Log.info(`Topos of region ${region.id}:`)
    // Log.info(region.polyTrimmedTopos)
    region.convexGroups = []
    for (let i = 0; i < region.polyTrimmed.length; i++) {
      if (region.polyTrimmedTopos[i].type === 'inner') continue // This is a hole

      let poly = region.polyTrimmed[i]
      let holes = []

      for (let j = 0; j < region.polyTrimmed.length; j++) {
        if (region.polyTrimmedTopos[j].parentId !== i) continue // Not a hole inside this parent poly
        let hole = region.polyTrimmed[j]
        holes.push(hole)
      }
      let shape = createShape(poly, holes, minHoleSize)

      let regionMesh
      let geometry = new ExtrudeGeometry(shape, extrudeSettings)
      if (finishColor) {
        let material = new MeshStandardMaterial({ color: finishColor, roughness: finishRoughness, wireframe })
        regionMesh = new Mesh(geometry, material)
      }
      else if (colorTopBottom) {
        regroupExtrudeGeometry(geometry)
        let material1 = new MeshStandardMaterial({ color: colors[region.id % colors.length], roughness: finishRoughness, wireframe })
        let material2 = new MeshStandardMaterial({ color: backColors[region.id % colors.length], roughness: finishRoughness, wireframe })
        let material3 = new MeshStandardMaterial({ color: colors[region.id % colors.length], roughness: finishRoughness, wireframe })
        regionMesh = new Mesh(geometry, [material1, material2, material3])
      } else {
        let material = new MeshStandardMaterial({ color: colors[region.id % colors.length], roughness: finishRoughness, wireframe })
        regionMesh = new Mesh(geometry, material)
      }

      regionMesh.name = getRegionName(region.id, i)
      model.add(regionMesh)

      // Compute convex decomposition
      let filteredHoles = holes.filter(hole => {
        let bbox: BoundingBoxNullable = {}
        for (let pt of hole) {
          bbox.min = min(bbox.min, pt)
          bbox.max = max(bbox.max, pt)
        }
        if (!isBboxDefined(bbox)) return false
        return bbox.max.x - bbox.min.x > minHoleSizeCollision || bbox.max.y - bbox.min.y > minHoleSizeCollision
      })
      let convexDecomp = convexDecomposition(poly, filteredHoles)
      let connectedMeshes = convexDecomp.map(poly => extrudePolygon(poly, thickness))
      region.convexGroups.push({
        name: getRegionName(region.id, i),
        meshes: connectedMeshes
      })

    }

  }

  // Create the bends
  for (let bend of bendInfo.bends) {

    for (let f = 0; f < bend.lineGroup.length; f++) {
      let line = bend.lineGroup[f]
      if (!line.stripes) throw new Error(`line in bend ${bend.id} has no stripes`)
      // Render a line segment (for debugging) 
      // let material = new LineBasicMaterial({
      //   color: 0x0000ff,
      //   linewidth: 2,
      // })

      // let points = []

      // points.push(new Vector3(bend.centerlineOfCurvature.start.x, bend.centerlineOfCurvature.start.y, bend.centerlineOfCurvature.start.z))
      // points.push(new Vector3(bend.centerlineOfCurvature.end.x, bend.centerlineOfCurvature.end.y, bend.centerlineOfCurvature.end.z))

      // let geometry = new BufferGeometry().setFromPoints(points)

      // let lineObj = new Line(geometry, material)
      // model.add(lineObj)


      // Render bend regions (for debugging)

      // let baRegionLeft = line['baRegion-left']
      // let baShapeLeft = createShape(baRegionLeft)
      // let baGeometryLeft = new ExtrudeGeometry(baShapeLeft, extrudeSettings)
      // let baMaterialLeft = new MeshStandardMaterial({ color: 0xff0000, wireframe })
      // let baPartLeft = new Mesh(baGeometryLeft, baMaterialLeft)
      // model.add(baPartLeft)


      // let baRegionRight = line['baRegion-right']
      // let baShapeRight =createShape(baRegionRight)
      // let baGeometryRight = new ExtrudeGeometry(baShapeRight, extrudeSettings)
      // let baMaterialRight = new MeshStandardMaterial({ color: 0xffff00, wireframe })
      // let baPartRight = new Mesh(baGeometryRight, baMaterialRight)
      // model.add(baPartRight)

      // Create the stripes
      for (let g = 0; g < line.stripes.length; g++) {
        for (let h = 0; h < line.stripes[g].length; h++) {
          let stripe = line.stripes[g][h]
          let stripeShape = createShape(stripe)
          let stripeGeometry = new ExtrudeGeometry(stripeShape, extrudeSettings)
          if (colorTopBottom) {
            regroupExtrudeGeometry(stripeGeometry)
          }

          // Save original coords, as we will apply transforms to them later
          stripe.originalCoords = stripeGeometry.attributes.position.array.slice()
          stripe.originalNormals = stripeGeometry.attributes.normal.array.slice()

          let stripeColor = '#90b8da'
          let stripeBackColor = '#90daa7'
          // If there are any matches between this array and the 
          if (selectedContours && matchArrayArraySlow(selectedContours.map(sc => sc.contourId), bend.originalContourIds)) {
            stripeColor = '#2244ff'
            stripeBackColor = stripeColor
          }

          let stripeMesh
          if (finishColor) {
            let material = new MeshStandardMaterial({ color: finishColor, roughness: finishRoughness, wireframe })
            stripeMesh = new Mesh(stripeGeometry, material)
          }
          else if (colorTopBottom) {
            let material1 = new MeshStandardMaterial({ color: stripeColor, roughness: finishRoughness, wireframe })
            let material2 = new MeshStandardMaterial({ color: stripeBackColor, roughness: finishRoughness, wireframe })
            let material3 = new MeshStandardMaterial({ color: stripeColor, roughness: finishRoughness, wireframe })
            stripeMesh = new Mesh(stripeGeometry, [material1, material2, material3])
          } else {
            let material = new MeshStandardMaterial({ color: stripeColor, roughness: finishRoughness, wireframe })
            stripeMesh = new Mesh(stripeGeometry, material)
          }

          // @ts-ignore The function signature says no arguments are expected, but I'm too afraid to remove `true`. I'm
          // not sure what will happen if I do.
          stripeGeometry.computeVertexNormals(true)
          // stripeGeometry.computeFaceNormals()

          stripeMesh.name = getBendStripeName(bend.id, f, g, h)
          model.add(stripeMesh)

          // Compute convex decomposition
          let convexDecomp = convexDecomposition(stripe)
          stripe.convexGroup = {
            name: getBendStripeName(bend.id, f, g, h),
            meshes: convexDecomp.map(poly => extrudePolygon(poly, thickness))
          }
        }

      }
    }
  }

  // Create all engrave lines
  for (let i = 0; i < engravePaths.length; i++) {
    let ePath = engravePaths[i]
    let material = new LineBasicMaterial({
      color: 0x000000
    })

    let points = []
    for (let pt of ePath.poly) {
      // points.push(cylindricalTransformPoint(new Vector3(pt.x, pt.y, thickness + 0.001)))
      points.push(new Vector3(pt.x, pt.y, thickness + 0.001))
    }

    let geometry = new BufferGeometry().setFromPoints(points)

    ePath.originalCoords = geometry.attributes.position.array.slice() 

    let engraveLine = new Line(geometry, material)

    engraveLine.name = `engrave-${i}`
    model.add(engraveLine)
  }

  for (let child of model.children) {
    child.matrixAutoUpdate = false
  }  

  if (!disableBendTransforms) {
    applyBendTransforms(model, arcified, percentBendTransform, percentBendTransformById)
  }
  model.matrixAutoUpdate = false
  return model
}

/**
 * Calculates the minimum flange length as measured between the bend center line and the outside edge of the minimum required flange.
 * @param {Number} thickness Thickness of the material
 * @param {Number} bendAngle Bend angle in radians
 * @param {Number} innerRadius Inner radius of the bend
 * @param {Number} kFactor Bend k-factor
 * @param {Number} dieEdgeRadius Radius of curvature of the V-gap edges
 * @param {Number} vGapCircleCenter V-gap of the die, as measured between the _centers_ of the circular v-gap edges. The returned flange length will not be less than half this value.
 */
export function calculateMinimumFlangeLength(thickness: number, bendAngle: number, innerRadius: number, kFactor: number, dieEdgeRadius: number, vGapCircleCenter: number) {

  let sinTheta2 = Math.sin(Math.abs(bendAngle) / 2)
  let cosTheta2 = Math.cos(Math.abs(bendAngle) / 2)
  let L = vGapCircleCenter - 2 * (dieEdgeRadius + thickness * (1 - kFactor)) * sinTheta2
  let b = 2 * (innerRadius + kFactor * thickness) * sinTheta2
  let a = (L - b) * 0.5
  let x = Math.min(a / cosTheta2, vGapCircleCenter * 3)

  let totalLength = 2 * x + Math.abs(bendAngle) * (innerRadius + thickness * kFactor)

  return Math.max(totalLength, vGapCircleCenter) * 0.5
  // TODO: Rewrite to use tan(theta/2)
}

/**
 * Creates a brake model from the given bends and part.
 * @param arcified The part
 * @param bends The bend or bends to create the brake model from. If multiple bends are given, it is assumed they are collinear.
 * @param toolSetups The tool setups to use for the brake model
 * @param param2 Options
 * @param param2.wireframe Whether to create the model in wireframe
 * @param param2.percentBendTransform Percentage to transform the bends
 * @param param2.percentBendTransformById For each bend, the percentage to transform that bend
 * @param param2.profile The name of the profile to use for the brake model.
 */
export function createBrakeModel(arcified: Arcified, bends: Bend[], toolSetups: ToolSetup[], {
  wireframe = false,
  minBrakeZ,
  maxBrakeZ,
  percentBendTransform = 100,
  percentBendTransformById = null,
  profile,
}: {
  wireframe?: boolean,
    minBrakeZ?: number,
    maxBrakeZ?: number,
  percentBendTransform?: number,
    percentBendTransformById?: Record<number, number> | null,
  profile?: 'collision_profile' | 'profile',
  }) {

  /**
   * Stackup:
   * 
   * punchHolder
   *   punchHolder.punch_datum
   *   punch.profile_mount_datum
   * punch
   *   punch.profile_tip_datum
   *   part (inside center of bend)   <--- ORIGIN
   * part
   *   part (use geometry to compute height)
   *   die.profile_center_datum
   * die
   *   die.profile_mount_datum
   *   dieHolder.die_datum
   * dieHolder
   *   dieHolder.machine_bed_datum
   *   machineBed.die_holder_datum
   * machineBed.profile
   * 
   */

  const colors = [
    '#1b9e77', // 0 Teal
    '#d95f02', // 1 Orange
    '#7570b3', // 2 Blue/Purple
    '#e7298a', // 3 Magenta
    '#66a61e', // 4 Green
    '#e6ab02', // 5 Yellow
    '#a6761d', // 6 Brown
    '#666666', // 7 Gray
    '#8dd3c7', // 8 Cyan
    '#ffffb3',
    '#bebada',
    '#fb8072',
    '#80b1d3',
    '#fdb462',
    '#b3de69',
    '#fccde5',
  ]

  const { partScale } = getPartMaterialProperties(arcified)

  if (!profile) throw new Error('profile must be specified')

  if (!Array.isArray(bends)) {
    bends = [bends]
  }

  const geometries = machines.find(g => g.machineId === toolSetups[0].punchDiePair.machine_id)?.geometries
  if (!geometries) {
    throw new Error(`Could not find geometries for machine with id ${toolSetups[0].punchDiePair.machine_id}`)
  }
  const {
    punchHolderOffset,
    dieDepth,
    dieHolderOffset,
    machineBedOffset,
    machineTopOffset
  } = getBrakeVectors(
    arcified,
    bends,
    toolSetups[0].punchDiePair,
    {
      percentBendTransform,
      percentBendTransformById,
    })

  // Verify that remaining tool setups are compatible with the first
  for (let i = 1; i < toolSetups.length; i++) {
    const {
      punchHolderOffset: _punchHolderOffset,
      dieDepth: _dieDepth,
      dieHolderOffset: _dieHolderOffset,
      machineBedOffset: _machineBedOffset,
      machineTopOffset: _machineTopOffset
    } = getBrakeVectors(
      arcified,
      bends,
      toolSetups[0].punchDiePair,
      {
        percentBendTransform,
        percentBendTransformById,
      })

    if (_punchHolderOffset.x !== punchHolderOffset.x || _punchHolderOffset.y !== punchHolderOffset.y) {
      throw new Error('punchHolderOffset does not match')
    }
    if (_dieDepth !== dieDepth) {
      throw new Error('dieDepth does not match')
    }
    if (_dieHolderOffset.x !== dieHolderOffset.x || _dieHolderOffset.y !== dieHolderOffset.y) {
      throw new Error('dieHolderOffset does not match')
    }
    if (_machineBedOffset.x !== machineBedOffset.x || _machineBedOffset.y !== machineBedOffset.y) {
      throw new Error('machineBedOffset does not match')
    }
    if (_machineTopOffset.x !== machineTopOffset.x || _machineTopOffset.y !== machineTopOffset.y) {
      throw new Error('machineTopOffset does not match')
    }
  }

  // Maximum extents of all tooling
  let minZ = Infinity
  let maxZ = -Infinity

  let brakeGroup = new Group()

  // Create tooling for each tool setup
  for (let toolSetup of toolSetups) {

    const { punch, die } = getPunchAndDie(toolSetup.punchDiePair)

    let {
      punchOffset,
      dieOffset,
    } = getBrakeVectors(
      arcified,
      bends,
      toolSetup.punchDiePair,
      {
        percentBendTransform,
        percentBendTransformById,
      })

    createPunchDieGeometry(profile, toolSetup.punchDiePair)

    for (let position of toolSetup.punchPositions) {
      let i = position.lineGroupIndex
      let zStart = position.zStart
      let zEnd = position.zEnd
      let bendId = position.bendId

      minZ = Math.min(minZ, zStart)
      maxZ = Math.max(maxZ, zEnd)
      minZ = Math.min(minZ, zEnd)
      maxZ = Math.max(maxZ, zStart)

      // PUNCH
      let punchMaterial = new MeshStandardMaterial({ color: position.color ?? '#aaaaaa', wireframe })
      let punchMesh = new Mesh(geometries.punches[profile][punch.id].geometry, punchMaterial)
      punchMesh.matrixAutoUpdate = false
      punchMesh.matrix.premultiply(new Matrix4().makeTranslation(punchOffset.x, punchOffset.y, zStart / (zEnd - zStart)))
      punchMesh.matrix.premultiply(new Matrix4().makeScale(partScale, partScale, zEnd - zStart))
      punchMesh.name = `punch-${bendId}.${i}`
      brakeGroup.add(punchMesh)
    }

    for (let position of toolSetup.diePositions) {
      let i = position.lineGroupIndex
      let zStart = position.zStart
      let zEnd = position.zEnd
      let bendId = position.bendId

      minZ = Math.min(minZ, zStart)
      maxZ = Math.max(maxZ, zEnd)

      // DIE
      let dieMaterial = new MeshStandardMaterial({ color: position.color ?? '#aaaaaa', wireframe })
      let dieMesh = new Mesh(geometries.dies[profile][die.id].geometry, dieMaterial)
      dieMesh.matrixAutoUpdate = false
      dieMesh.matrix.premultiply(new Matrix4().makeTranslation(dieOffset.x, dieOffset.y, zStart / (zEnd - zStart)))
      dieMesh.matrix.premultiply(new Matrix4().makeScale(partScale, partScale, zEnd - zStart))
      dieMesh.name = `die-${bendId}.${i}`
      brakeGroup.add(dieMesh)

      // Add cylinders for die radius (for debugging)
      // let vGapCircleCenter = Math.abs(die.v_gap_edge.center.x - die.profile_center_datum.x) * 2
      // let cylGeometry = new CylinderGeometry(die.v_gap_edge.radius, die.v_gap_edge.radius, 1, 80)
      // let cylMaterial = new MeshStandardMaterial({ color: '#ffff00' })
      // let cylMesh = new Mesh(cylGeometry, cylMaterial)
      // let cylOffset = add(
      //   die.v_gap_edge.center,
      //   mul(die.profile_center_datum, -1)
      // )
      // cylMesh.matrixAutoUpdate = false
      // cylMesh.matrix.premultiply(new Matrix4().makeRotationX(Math.PI / 2))
      // cylMesh.matrix.premultiply(new Matrix4().makeTranslation(0, 0, 0.5))
      // cylMesh.matrix.premultiply(new Matrix4().makeTranslation(cylOffset.x, cylOffset.y, zStart / (zEnd - zStart)))
      // cylMesh.matrix.premultiply(new Matrix4().makeScale(partScale, partScale, zEnd - zStart))
      // brakeGroup.add(cylMesh)

      // let cylMesh2 = new Mesh(cylGeometry, cylMaterial)
      // let cylOffset2 = add(
      //   die.v_gap_edge.center,
      //   { x: vGapCircleCenter, y: 0 },
      //   mul(die.profile_center_datum, -1)
      // )
      // cylMesh2.matrixAutoUpdate = false
      // cylMesh2.matrix.premultiply(new Matrix4().makeRotationX(Math.PI / 2))
      // cylMesh2.matrix.premultiply(new Matrix4().makeTranslation(0, 0, 0.5))
      // cylMesh2.matrix.premultiply(new Matrix4().makeTranslation(cylOffset2.x, cylOffset2.y, zStart / (zEnd - zStart)))
      // cylMesh2.matrix.premultiply(new Matrix4().makeScale(partScale, partScale, zEnd - zStart))
      // brakeGroup.add(cylMesh2)
    }
  }

  if (minBrakeZ != null && maxBrakeZ != null) {
    // Set brake extents to specified values
    minZ = minBrakeZ
    maxZ = maxBrakeZ
  } else {
    // Extend brake another 36 inches in each direction
    maxZ += 36 * partScale
    minZ -= 36 * partScale
  }

  // PUNCH HOLDER 
  let punchHolderMaterial = new MeshStandardMaterial({ color: '#777777', wireframe })
  let punchHolderMesh = new Mesh(geometries.punchHolder[profile].geometry, punchHolderMaterial)
  punchHolderMesh.matrixAutoUpdate = false
  punchHolderMesh.matrix.premultiply(new Matrix4().makeTranslation(punchHolderOffset.x, punchHolderOffset.y, minZ / (maxZ - minZ)))
  punchHolderMesh.matrix.premultiply(new Matrix4().makeScale(partScale, partScale, maxZ - minZ))
  punchHolderMesh.name = `punchHolder`
  brakeGroup.add(punchHolderMesh)

  // DIE HOLDER
  let dieHolderMaterial = new MeshStandardMaterial({ color: '#777777', wireframe })
  let dieHolderMesh = new Mesh(geometries.dieHolder[profile].geometry, dieHolderMaterial)
  dieHolderMesh.matrixAutoUpdate = false
  dieHolderMesh.matrix.premultiply(new Matrix4().makeTranslation(dieHolderOffset.x, dieHolderOffset.y, minZ / (maxZ - minZ)))
  dieHolderMesh.matrix.premultiply(new Matrix4().makeScale(partScale, partScale, maxZ - minZ))
  dieHolderMesh.name = `dieHolder`
  brakeGroup.add(dieHolderMesh)

  // MACHINE BED
  let machineBedMaterial = new MeshStandardMaterial({ color: '#333333', wireframe })
  let machineBedMesh = new Mesh(geometries.machineBed[profile].geometry, machineBedMaterial)
  machineBedMesh.matrixAutoUpdate = false
  machineBedMesh.matrix.premultiply(new Matrix4().makeTranslation(machineBedOffset.x, machineBedOffset.y, minZ / (maxZ - minZ)))
  machineBedMesh.matrix.premultiply(new Matrix4().makeScale(partScale, partScale, maxZ - minZ))
  machineBedMesh.name = `machineBed`
  brakeGroup.add(machineBedMesh)

  // MACHINE TOP
  let machineTopMaterial = new MeshStandardMaterial({ color: '#333333', wireframe })
  let machineTopMesh = new Mesh(geometries.machineTop[profile].geometry, machineTopMaterial)
  machineTopMesh.matrixAutoUpdate = false
  machineTopMesh.matrix.premultiply(new Matrix4().makeTranslation(machineTopOffset.x, machineTopOffset.y, minZ / (maxZ - minZ)))
  machineTopMesh.matrix.premultiply(new Matrix4().makeScale(partScale, partScale, maxZ - minZ))
  machineTopMesh.name = `machineTop`
  brakeGroup.add(machineTopMesh)

  brakeGroup.name = 'brake'
  brakeGroup.matrixAutoUpdate = false
  brakeGroup.updateMatrixWorld(true)


  return { brakeGroup, dieDepth }
}

/**
 * Create the geometry for a punch and die and the given profile, if it has not been created yet.
 * @param profile The profile to use for the geometry
 * @param punch The punch to create the geometry for
 * @param die The die to create the geometry for
 */
function createPunchDieGeometry(profile: 'collision_profile' | 'profile', punchDiePair: BrakePunchDie) {
  const geometries = machines.find(g => g.machineId === punchDiePair.machine_id)?.geometries
  if (!geometries) {
    throw new Error(`Could not find geometries for machine with id ${punchDiePair.machine_id}`)
  }
  const { punch, die } = getPunchAndDie(punchDiePair)

  // Create geometries if they have not been created yet
  if (!geometries.punches[profile][punch.id]) {
    let punchShape = createShape(punch[profile])
    let geometry = new ExtrudeGeometry(punchShape, geometries.extrudeSettings)
    let convexDecomp = convexDecomposition(punch[profile])
    let convexGeoms = convexDecomp.map(decomp =>
      new ExtrudeGeometry(createShape(decomp), geometries.extrudeSettings)
    )
    geometries.punches[profile][punch.id] = {
      geometry,
      convexDecomp,
      convexGeoms
    }
  }

  if (!geometries.dies[profile][die.id]) {
    let dieShape = createShape(die[profile])
    let geometry = new ExtrudeGeometry(dieShape, geometries.extrudeSettings)
    let convexDecomp = convexDecomposition(die[profile])
    let convexGeoms = convexDecomp.map(decomp =>
      new ExtrudeGeometry(createShape(decomp), geometries.extrudeSettings)
    )
    geometries.dies[profile][die.id] = {
      geometry,
      convexDecomp,
      convexGeoms
    }
  }
}


/**
 * Creates a brake model from the given bends and part.
 * @param arcified The part
 * @param bends The bend or bends to create the brake model from. If multiple bends are given, it is assumed they are collinear.
 * @param param2 Options
 * @param param2.percentBendTransform Percentage to transform the bends
 * @param param2.percentBendTransformById For each bend, the percentage to transform that bend
 * @param param2.reversed Whether the part is aligned in the reverse direction along the bend
 * @param param2.dieShorten Shorten die and punch by this much on each end, default is 0.125"
 * @param param2.profile The name of the profile to use for the brake model.
 */
export function createBrakeConvexGroups(
  arcified: Arcified,
  bends: Bend[],
  toolSetups: ToolSetup[],
  {
    percentBendTransform = 100,
    percentBendTransformById = null,
    profile,
    minBrakeZ,
    maxBrakeZ,
  }: {
    percentBendTransform?: number,
      percentBendTransformById?: Record<number, number> | null,
    profile?: 'collision_profile' | 'profile',
    minBrakeZ?: number,
    maxBrakeZ?: number,
  }) {

  const { partScale } = getPartMaterialProperties(arcified)

  if (!profile) throw new Error('profile must be specified')

  if (!Array.isArray(bends)) {
    bends = [bends]
  }

  const geometries = machines.find(g => g.machineId === toolSetups[0].punchDiePair.machine_id)?.geometries
  if (!geometries) {
    throw new Error(`Could not find geometries for machine with id ${toolSetups[0].punchDiePair.machine_id}`)
  }
  const {
    punchHolderOffset,
    dieDepth,
    dieHolderOffset,
    machineBedOffset,
    machineTopOffset
  } = getBrakeVectors(
    arcified,
    bends,
    toolSetups[0].punchDiePair,
    {
      percentBendTransform,
      percentBendTransformById,
    })

  // Verify that remaining tool setups are compatible with the first
  for (let i = 1; i < toolSetups.length; i++) {
    const {
      punchHolderOffset: _punchHolderOffset,
      dieDepth: _dieDepth,
      dieHolderOffset: _dieHolderOffset,
      machineBedOffset: _machineBedOffset,
      machineTopOffset: _machineTopOffset
    } = getBrakeVectors(
      arcified,
      bends,
      toolSetups[0].punchDiePair,
      {
        percentBendTransform,
        percentBendTransformById,
      })

    if (_punchHolderOffset.x !== punchHolderOffset.x || _punchHolderOffset.y !== punchHolderOffset.y) {
      throw new Error('punchHolderOffset does not match')
    }
    if (_dieDepth !== dieDepth) {
      throw new Error('dieDepth does not match')
    }
    if (_dieHolderOffset.x !== dieHolderOffset.x || _dieHolderOffset.y !== dieHolderOffset.y) {
      throw new Error('dieHolderOffset does not match')
    }
    if (_machineBedOffset.x !== machineBedOffset.x || _machineBedOffset.y !== machineBedOffset.y) {
      throw new Error('machineBedOffset does not match')
    }
    if (_machineTopOffset.x !== machineTopOffset.x || _machineTopOffset.y !== machineTopOffset.y) {
      throw new Error('machineTopOffset does not match')
    }
  }

  // Maximum extents of all tooling
  let minZ = Infinity
  let maxZ = -Infinity

  let dies: ConvexGroup[] = []
  let punches: ConvexGroup[] = []

  const scalePolygon = (poly: Vector[]) => {
    return poly.map(p => {
      return { x: p.x * partScale, y: p.y * partScale }
    })
  }

  // Create tooling for each tool setup
  for (let toolSetup of toolSetups) {

    const { punch, die } = getPunchAndDie(toolSetup.punchDiePair)

    let {
      punchOffset,
      dieOffset,
    } = getBrakeVectors(
      arcified,
      bends,
      toolSetup.punchDiePair,
      {
        percentBendTransform,
        percentBendTransformById,
      })

    let diePositions = toolSetup.diePositions
    let punchPositions = toolSetup.punchPositions

    createPunchDieGeometry(profile, toolSetup.punchDiePair)

    for (let position of punchPositions) {
      let i = position.lineGroupIndex
      let zStart = position.zStart
      let zEnd = position.zEnd
      let bendId = position.bendId

      minZ = Math.min(minZ, zStart)
      maxZ = Math.max(maxZ, zEnd)
      minZ = Math.min(minZ, zEnd)
      maxZ = Math.max(maxZ, zStart)

      // PUNCH
      punches.push({
        name: `punch-${bendId}.${i}`,
        meshes: geometries.punches[profile][punch.id].convexDecomp.map(poly => extrudePolygon(scalePolygon(poly), zEnd - zStart)),
        matrix: getMatrix4FromTHREEMatrix4(new Matrix4().makeTranslation(punchOffset.x * partScale, punchOffset.y * partScale, zStart))
      })
    }

    for (let position of diePositions) {
      let i = position.lineGroupIndex
      let zStart = position.zStart
      let zEnd = position.zEnd
      let bendId = position.bendId

      minZ = Math.min(minZ, zStart)
      maxZ = Math.max(maxZ, zEnd)

      // DIE
      dies.push({
        name: `die-${bendId}.${i}`,
        meshes: geometries.dies[profile][die.id].convexDecomp.map(poly => extrudePolygon(scalePolygon(poly), zEnd - zStart)),
        matrix: getMatrix4FromTHREEMatrix4(new Matrix4().makeTranslation(dieOffset.x * partScale, dieOffset.y * partScale, zStart))
      })
    }
  }

  if (minBrakeZ != null && maxBrakeZ != null) {
    // Set brake extents to specified values
    minZ = minBrakeZ
    maxZ = maxBrakeZ
  } else {
    // Extend brake another 36 inches in each direction
    maxZ += 36 * partScale
    minZ -= 36 * partScale
  }

  // PUNCH HOLDER
  let punchHolder: ConvexGroup = {
    name: 'punchHolder',
    meshes: geometries.punchHolder[profile].convexDecomp.map(poly => extrudePolygon(scalePolygon(poly), maxZ - minZ)),
    matrix: getMatrix4FromTHREEMatrix4(new Matrix4().makeTranslation(punchHolderOffset.x * partScale, punchHolderOffset.y * partScale, minZ))
  }

  // DIE HOLDER
  let dieHolder: ConvexGroup = {
    name: 'dieHolder',
    meshes: geometries.dieHolder[profile].convexDecomp.map(poly => extrudePolygon(scalePolygon(poly), maxZ - minZ)),
    matrix: getMatrix4FromTHREEMatrix4(new Matrix4().makeTranslation(dieHolderOffset.x * partScale, dieHolderOffset.y * partScale, minZ))
  }

  // MACHINE BED
  let machineBed: ConvexGroup = {
    name: 'machineBed',
    meshes: geometries.machineBed[profile].convexDecomp.map(poly => extrudePolygon(scalePolygon(poly), maxZ - minZ)),
    matrix: getMatrix4FromTHREEMatrix4(new Matrix4().makeTranslation(machineBedOffset.x * partScale, machineBedOffset.y * partScale, minZ))
  }

  // MACHINE TOP
  let machineTop: ConvexGroup = {
    name: 'machineTop',
    meshes: geometries.machineTop[profile].convexDecomp.map(poly => extrudePolygon(scalePolygon(poly), maxZ - minZ)),
    matrix: getMatrix4FromTHREEMatrix4(new Matrix4().makeTranslation(machineTopOffset.x * partScale, machineTopOffset.y * partScale, minZ))
  }

  return {
    punchHolder,
    punches,
    dies,
    dieHolder,
    machineBed,
    machineTop,
    allConvexGroups: [punchHolder, ...punches, ...dies, dieHolder, machineBed, machineTop]
  }

}

/**
 * Gets the die depth in part native units, from the given bend(s) and part.
 * @param arcified The part
 * @param bends The bend or bends to get the tooling positions for. If multiple bends are given, it is assumed they are collinear.
 * @param param2 Options
 * @param param2.percentBendTransform Percentage to transform the bends
 * @param param2.percentBendTransformById For each bend, the percentage to transform that bend
 */
export function getDieDepth(arcified: Arcified, bends: Bend[], punchDiePair: BrakePunchDie, {
  percentBendTransform = 100,
  percentBendTransformById = null,
}: {
  percentBendTransform?: number,
  percentBendTransformById?: any,
  }) {

  if (!Array.isArray(bends)) {
    bends = [bends]
  }

  const { die } = getPunchAndDie(punchDiePair)

  let { innerRadius, thickness, partScale } = getPartMaterialBendProperties(arcified)
  const diePartPunchMargin = DIE_PART_PUNCH_MARGIN_INCH * partScale

  let bendAngle = bends[0].angle
  if (bendAngle == null) throw new Error('bend has no angle')
  if (bendAngle > 179) bendAngle = 179
  if (bendAngle < -179) bendAngle = -179

  let lambda = (180 - Math.abs(bendAngle) * (percentBendTransform ?? 100) / 100 * (percentBendTransformById?.[bends[0].id] ?? 100) / 100) / 2 / 180 * Math.PI

  // "Effective" v-gap depends on the current angle. As the angle increases, the effective v-gap decreases as the point
  // of contact moves down the circular edge of the die. The point of contact "rolls down" the edge vertically, also
  // affecting the die depth.
  let vGapCircleCenterInch = Math.abs(die.v_gap_edge.center.x - die.profile_center_datum.x) * 2
  let vGapEffInch = vGapCircleCenterInch - 2 * (die.v_gap_edge.radius * Math.cos(lambda))
  let vertRollDownInch = die.v_gap_edge.radius * (1 - Math.sin(lambda))

  let c = (innerRadius + thickness) * (1 / Math.sin(lambda) - 1)
  let d = 0.5 * vGapEffInch * partScale / Math.tan(lambda) - (innerRadius + thickness + c)
  let dieDepth = innerRadius + d + vertRollDownInch * partScale - diePartPunchMargin * 2 // Add margin on both top and bottom of part (* 2)

  return dieDepth
}

/**
 * Gets the tooling positions from the given bend(s) and part. All vectors are returned in inches. The single scalar
 * quantity, dieDepth, is returned in part native units.
 * @param arcified The part
 * @param bends The bend or bends to get the tooling positions for. If multiple bends are given, it is assumed they are collinear.
 * @param param2 Options
 * @param param2.percentBendTransform Percentage to transform the bends
 * @param param2.percentBendTransformById For each bend, the percentage to transform that bend
 * @param param2.reversed Whether the part is aligned in the reverse direction along the bend
 * @param param2.dieShorten Shorten die and punch by this much on each end, default is 0.125"
 */
export function getBrakeVectors(
  arcified: Arcified,
  bends: Bend[],
  punchDiePair: BrakePunchDie,
  {
    percentBendTransform = 100,
    percentBendTransformById = null,
  }: {
    percentBendTransform?: number,
    percentBendTransformById?: Record<number, number> | null,
  }) {

  /**
   * Stackup:
   * 
   * punchHolder
   *   punchHolder.punch_datum
   *   punch.profile_mount_datum
   * punch
   *   punch.profile_tip_datum
   *   part (inside center of bend)   <--- ORIGIN
   * part
   *   part (use geometry to compute height)
   *   die.profile_center_datum
   * die
   *   die.profile_mount_datum
   *   dieHolder.die_datum
   * dieHolder
   *   dieHolder.machine_bed_datum
   *   machineBed.die_holder_datum
   * machineBed.profile
   * 
   * machineTop (relative to machine bed)
   *  machineBed.machine_bed_datum  
   */

  const machine = machines.find(m => m.machineId === punchDiePair.machine_id)
  if (!machine) {
    throw new Error(`Could not find machine with id ${punchDiePair.machine_id}`)
  }
  const tooling = machine.tooling

  if (!Array.isArray(bends)) {
    bends = [bends]
  }

  let { partScale } = getPartMaterialProperties(arcified)

  let { punch, die } = getPunchAndDie(punchDiePair)

  let dieDepth = getDieDepth(arcified, bends, punchDiePair, {
    percentBendTransform,
    percentBendTransformById,
  })
  let dieDepthOffset = { x: 0, y: dieDepth }
  let punchDepthOffset = mul(dieDepthOffset, -1)

  // PUNCH
  let punchOffsetInch = add(
    mul(punch.profile_tip_datum, -1),
    mul(punchDepthOffset, 1 / partScale)
  )

  // DIE
  let dieOffsetInch = add(
    mul(die.profile_center_datum, -1)
  )

  // PUNCH HOLDER 
  let punchHolderOffsetInch = add(
    mul(punch.profile_tip_datum, -1),
    punch.profile_mount_datum,
    mul(tooling.punchHolder.punch_datum, -1),
    mul(punchDepthOffset, 1 / partScale)
  )

  // DIE HOLDER
  let dieHolderOffsetInch = add(
    mul(die.profile_center_datum, -1),
    die.profile_mount_datum,
    mul(tooling.dieHolder.die_datum, -1),
  )

  // MACHINE BED
  let machineBedOffsetInch = add(
    mul(die.profile_center_datum, -1),
    die.profile_mount_datum,
    mul(tooling.dieHolder.die_datum, -1),
    tooling.dieHolder.machine_bed_datum,
    mul(tooling.machineBed.die_holder_datum, -1),
  )

  // MACHINE TOP
  let machineTopOffsetInch = add(
    mul(die.profile_center_datum, -1),
    die.profile_mount_datum,
    mul(tooling.dieHolder.die_datum, -1),
    tooling.dieHolder.machine_bed_datum,
    mul(tooling.machineBed.die_holder_datum, -1),
    // tooling.machineBed.machine_bed_datum, // I think this is zero?
    mul(tooling.machineTop.machine_bed_datum, -1),
  )

  // FRONT LASER EYE
  let frontLaserEyeOffsetInch = add(
    machineBedOffsetInch,
    mul(tooling.frontLaserEye.machine_bed_datum, -1),
  )

  // REAR LASER EYE
  let rearLaserEyeOffsetInch = add(
    machineBedOffsetInch,
    mul(tooling.rearLaserEye.machine_bed_datum, -1),
  )

  return {
    punchHolderOffset: punchHolderOffsetInch,
    punchOffset: punchOffsetInch,
    dieDepth,
    dieOffset: dieOffsetInch,
    dieHolderOffset: dieHolderOffsetInch,
    machineBedOffset: machineBedOffsetInch,
    machineTopOffset: machineTopOffsetInch,
    frontLaserEyeOffset: frontLaserEyeOffsetInch,
    rearLaserEyeOffset: rearLaserEyeOffsetInch,
  }
}


/**
 * Gets the tooling positions from the given bend(s) and part. By default, tools are placed at the full length of each
 * punch line, but this can be altered using dieShorten, minDieLength, and minDieLengthPct.
 * @param bends The bend or bends to get the tooling positions for. If multiple bends are given, it is assumed they are collinear.
 * @param param2 Options
 */
export function getToolingPositions(
  bends: Bend[],
  {
    reversed,
    offset = 0,
    dieShorten = 0,
    minDieLength = 0,
    minDieLengthPct = 0,
    justify,
  }: {
    /** Whether the part is aligned in the reverse direction along the bend */
    reversed?: boolean,
    /** The offset to apply to the positions, in part native units. */
    offset?: number
    /**
     * Shorten die and punch by this amount on each end, in part native units. Default is 0, which is no shortening.
     */
    dieShorten?: number,
    /**
     * Enforce a minimum length of the tool, in part native units. The tool could become longer than the punch line.
     * Default is 0, which is no minimum.
     */
    minDieLength?: number
    /** Enforce a minimum length of the tool as a percentage of the punch line. Default is 0, which is no minimum. */
    minDieLengthPct?: number
    /**
     * A nested array specifying where to position the punch and die relative to the punchline. If the die and punch are
     * longer than the punchline, this can be used to control which end of the punchline the overflow is on. There must
     * be one item in justify for each bend, and one subitem in each of those items for each line in the bend's line
     * group. Default is 'center'.
     */
    justify?: ('start' | 'center' | 'end')[][]
  }) {


  if (!Array.isArray(bends)) {
    bends = [bends]
  }

  // The origin will be placed at bends[0].lineGroup[0].start
  let refStart = bends[0].lineGroup[0].start
  let refEnd = bends[0].lineGroup[0].end

  if (reversed) {
    let temp = refStart
    refStart = refEnd
    refEnd = temp
  }

  // lineDir is the normalized direction from bends[0].lineGroup[0].start to bends[0].lineGroup[0].end
  let lineDir = normalize(sub(refEnd, refStart))

  // Translate tooling so it is centered around the origin
  let minDot = Infinity
  let maxDot = -Infinity
  for (let bend of bends) {
    for (let line of bend.lineGroup) {
      minDot = Math.min(minDot, dot(lineDir, line.start), dot(lineDir, line.end))
      maxDot = Math.max(maxDot, dot(lineDir, line.start), dot(lineDir, line.end))
    }
  }
  let midDot = (minDot + maxDot) / 2

  // Extents of tooling
  let maxZ = -Infinity, minZ = Infinity

  // Add a punch and die to each bend line of each bend
  let positions: ToolingPosition[] = []

  if (justify && justify.length !== bends.length) {
    throw new Error('justify must have the same number of items as bends')
  }

  for (let h = 0; h < bends.length; h++) {
    let bend = bends[h]

    if (justify && justify[h].length !== bend.lineGroup.length) {
      throw new Error(`justify[${h}] must have the same number of items as bends[${h}].lineGroup`)
    }

    for (let i = 0; i < bend.lineGroup.length; i++) {

      let adjustedLine = adjustPunchline(bend.lineGroup[i], { dieShorten, minDieLength, minDieLengthPct, justify: justify?.[h][i] })

      let zStart = dot(lineDir, adjustedLine.start) - midDot
      let zEnd = dot(lineDir, adjustedLine.end) - midDot

      // No guarantee that bend.lineGroup[i] is in the same direction as lineDir (which is bend.lineGroup[0]), so we
      // can't use `reversed` here. Instead, just swap them if they are in the wrong order.
      if (zStart > zEnd) {
        let temp = zStart
        zStart = zEnd
        zEnd = temp
      }

      zStart += offset
      zEnd += offset

      maxZ = Math.max(maxZ, zStart)
      maxZ = Math.max(maxZ, zEnd)
      minZ = Math.min(minZ, zStart)
      minZ = Math.min(minZ, zEnd)

      positions.push({
        bendId: bend.id,
        lineGroupIndex: i,
        zStart,
        zEnd
      })
    }
  }

  return { positions, minZ, maxZ }
}

/**
 * Lengthens or shortens a punchline according to the given parameters, and returns a new line.
 */
function adjustPunchline({
  start,
  end
}: {
  start: Vector,
  end: Vector,
}, {
  dieShorten = 0,
  minDieLength = 0,
  minDieLengthPct = 0,
  justify = 'center',
}: {
  /**
    * Shorten the line by this amount on each end, in part native units. Default is 0, which is no shortening.
    */
  dieShorten?: number,
  /**
    * Enforce a minimum length of the punch line, in part native units. The line could become longer. Default is 0,
    * which is no minimum.
    */
  minDieLength?: number
  /**
   * Enforce a minimum length of the adjusted punchline as a percentage of the original punch line. Default is 0, which
   * is no minimum.
   */
  minDieLengthPct?: number
  /**
   * If the die and punch are longer than the punchline, where to position the punch and die relative to the
   * punchline. This can be used to control which end of the punchline the overflow is on. Default is 'center'.
   */
  justify?: 'start' | 'center' | 'end'
}) {

  const lineDir = normalize(sub(end, start))

  let zStart = dot(lineDir, start)
  let zEnd = dot(lineDir, end)

  let actualLength = (zEnd - zStart) - dieShorten * 2
  if (minDieLengthPct > 0) {
    actualLength = Math.max(actualLength, (zEnd - zStart) * minDieLengthPct / 100)
  }
  if (minDieLength > 0) {
    actualLength = Math.max(actualLength, minDieLength)
  }

  let lengthDiff = (zEnd - zStart) - actualLength
  let newZStart, newZEnd
  if (lengthDiff < 0 && justify === 'start') {
    newZStart = zStart
    newZEnd = zEnd - lengthDiff
  } else if (lengthDiff < 0 && justify === 'end') {
    newZStart = zStart + lengthDiff
    newZEnd = zEnd
  } else {
    newZStart = zStart + lengthDiff / 2
    newZEnd = zEnd - lengthDiff / 2
  }

  return {
    start: add(start, mul(lineDir, newZStart - zStart)),
    end: add(start, mul(lineDir, newZEnd - zStart)),
  }
}

/**
 * Returns a transform matrix that can be applied to a die/punch to position them around the given bend. By adjusting
 * percentBendTransformById, the transform will change as the part is bent.
 * @param bend The bend to get the transform for
 * @param arcified Arcified part
 * @param percentBendTransform Scale each bend angle by this percentage
 * @param percentBendTransformById Scale each bend angle by its own percentage
 * @param reversed Whether to align the bend in the reverse direction
 * @returns A transform matrix to align the die/punch around the part at the given bend.
 */
export function getBendAlignedTransform(bends: Bend[], arcified: Arcified, percentBendTransform: number, percentBendTransformById: Record<number, number>, reversed: boolean) {

  const { thickness } = getPartMaterialProperties(arcified)

  const bend = bends[0]

  if (!bend.bendStack) throw new Error(`Bend ${bend.id} has no bendStack`)
  if (bend.angle == null) throw new Error(`Bend ${bend.id} has no angle`)
  if (!arcified.bendInfo) throw new Error(`Arcified part has no bendInfo`)

  // Get combined transform up to (but not including) this bend, using the standard method:
  let mat = getBendStackMatrix(bend.bendStack, arcified.bendInfo.bends, arcified, percentBendTransform, percentBendTransformById)
  let matRotation = new Matrix4()
  matRotation.extractRotation(mat)

  // Get transform of this bend
  let matCurrent = getBendStackMatrix([bend.id], arcified.bendInfo.bends, arcified, percentBendTransform, percentBendTransformById)
  matCurrent.premultiply(mat) // Apply previous transforms

  let matCurrentRotation = new Matrix4()
  matCurrentRotation.extractRotation(matCurrent)

  // Construct line along these bends
  let lineDirFlat = normalize(sub(bend.lineGroup[0].end, bend.lineGroup[0].start))
  let minPoint = bend.lineGroup[0].start
  let maxPoint = bend.lineGroup[0].end
  let minDot = Infinity
  let maxDot = -Infinity
  for (let b of bends) {
    for (let line of b.lineGroup) {
      let bDotStart = dot(lineDirFlat, line.start)
      if (bDotStart < minDot) {
        minPoint = line.start
        minDot = bDotStart
      }
      if (bDotStart > maxDot) {
        maxPoint = line.start
        maxDot = bDotStart
      }
      let bDotEnd = dot(lineDirFlat, line.end)
      if (bDotEnd < minDot) {
        minPoint = line.end
        minDot = bDotEnd
      }
      if (bDotEnd > maxDot) {
        maxPoint = line.end
        maxDot = bDotEnd
      }
    }
  }
  // NOTE: The z-component is not exactly correct, but it seems to be close enough

  let lineStart = new Vector3(
    minPoint.x,
    minPoint.y,
    bend.angle < 0 ? 0 : thickness,
  )
  let lineEnd = new Vector3(
    maxPoint.x,
    maxPoint.y,
    bend.angle < 0 ? 0 : thickness,
  )

  // Reverse line start and end if reversed
  if (reversed) {
    let temp = lineStart
    lineStart = lineEnd
    lineEnd = temp
  }

  let cylinderTransformFunction = getCylinderTransformFunction(bend, arcified, percentBendTransform, percentBendTransformById)
  lineStart = cylinderTransformFunction(lineStart)
  lineEnd = cylinderTransformFunction(lineEnd)

  // upFrontDir
  let upFrontDir = new Vector3(0, 0, bend.angle > 0 ? 1 : -1)
  let upBackDir = upFrontDir.clone()

  // Apply transforms
  upFrontDir.applyMatrix4(matRotation)
  upBackDir.applyMatrix4(matCurrentRotation)

  // Align the upVector to the "middle" of the bend, so that the bend always makes an upright "V"
  // The sum of upFrontDir and upBackDir should be the "middle" of the bend
  let upDir = upFrontDir.clone().add(upBackDir).normalize()

  // upDir is not quite right--it should be normal to lineDir but it's not.

  // Transform line endpoints and upVector by the bend stack matrix to put them in final position
  lineStart.applyMatrix4(mat)
  lineEnd.applyMatrix4(mat)

  // Direction from start to end
  let lineDir = lineEnd.clone().sub(lineStart).normalize()

  // Align model so that we are looking straight down lineDir, with upVector being up
  let rightDir = lineDir.clone().cross(upDir)
  rightDir.multiplyScalar(-1)

  let bendAlignedMat = new Matrix4().makeBasis(rightDir, upDir, lineDir)

  let lineMid = lineStart.clone().add(lineEnd).multiplyScalar(0.5)
  bendAlignedMat.setPosition(lineMid)

  return bendAlignedMat

}

/**
 * Applies bend transforms to a group of meshes create using createBendModel
 * @param model Group of meshes to apply transforms too
 * @param arcified The part
 * @param percentBendTransform Percentage of transforms to apply
 * @param percentBendTransformById An object containing a property for each bend, whose key is the bend id and whose value is a number from 0 to 100+ indicating the percentage of each bend to apply
 */
export function applyBendTransforms(model: Group, arcified: Arcified, percentBendTransform = 100, percentBendTransformById?: Record<number, number> | null) {

  if (!arcified?.bendInfo) return
  let bendInfo = arcified.bendInfo
  let engravePaths = bendInfo.engravePaths
  const { thickness, innerRadius } = getPartMaterialBendProperties(arcified)

  if (!engravePaths) throw new Error(`bendInfo has no engravePaths`)

  // Apply transform matrices to all parts

  // Apply to regions
  for (let region of bendInfo.regions) {
    if (!region.bendStack) throw new Error(`Region ${region.id} has no bend stack`)
    if (!region.polyTrimmed) throw new Error(`Region ${region.id} has no polyTrimmed`)
    if (!region.polyTrimmedTopos) throw new Error(`Region ${region.id} has no polyTrimmedTopos`)
    if (!region.convexGroups) throw new Error(`Region ${region.id} has no convexGroups`)

    let mat = getBendStackMatrix(region.bendStack, bendInfo.bends, arcified, percentBendTransform, percentBendTransformById)
    region.convexGroups?.forEach(cg => cg.matrix = getMatrix4FromTHREEMatrix4(mat))

    for (let i = 0; i < region.polyTrimmed.length; i++) {
      if (region.polyTrimmedTopos[i].type === 'inner') continue // This is a hole
      let regionMeshName = getRegionName(region.id, i)
      let regionMesh = model.getObjectByName(regionMeshName)
      if (regionMesh) regionMesh.matrix.copy(mat)
    }

    // Apply to engrave paths
    let engravePathsThisRegion = engravePaths.filter(ePath => ePath.parent === region)
    for (let ePath of engravePathsThisRegion) {
      let engraveLineName = `engrave-${engravePaths.indexOf(ePath)}`
      let engraveLine = model.getObjectByName(engraveLineName)
      if (engraveLine) engraveLine.matrix.copy(mat)
    }
  }

  const vert = new Vector3()
  const vert2 = new Vector3()

  // Apply to bends
  for (let bend of bendInfo.bends) {
    if (!bend.bendStack) throw new Error(`Bend ${bend.id} has no bend stack`)
    if (!bend.normal || bend.allowance == null) throw new Error(`Bend ${bend.id} has no convenience properties`)
    if (bend.angle == null) throw new Error(`Bend ${bend.id} has no angle`)

    const cylindricalTransformPoint = getCylinderTransformFunction(bend, arcified, percentBendTransform, percentBendTransformById)
    const cylindricalTransformNormal = getCylinderNormalTransformFunction(bend, arcified, percentBendTransform, percentBendTransformById)
    let mat = getBendStackMatrix(bend.bendStack, bendInfo.bends, arcified, percentBendTransform, percentBendTransformById)

    // Compute center line of curvature
    let fractionBendTransform = percentBendTransform / 100 * (percentBendTransformById?.[bend.id] ?? 100) / 100
    let normal = bend.normal
    // Point at which to begin the turn, relative to the bend line
    let offsetTurnStart
    if (bend.parentSide === 'right') {
      normal = mul(normal, -1)
    }
    offsetTurnStart = mul(normal, bend.allowance * 0.5 * fractionBendTransform)
    let sideLine = {
      start: add(bend.lineGroup[0].start, offsetTurnStart),
      end: add(bend.lineGroup[0].end, offsetTurnStart)
    }

    let centerlineOfCurvature = { start: { ...sideLine.start, z: 0 }, end: { ...sideLine.end, z: 0 } }
    if (bend.angle > 0) {
      centerlineOfCurvature.start.z = innerRadius + thickness
      centerlineOfCurvature.end.z = innerRadius + thickness
    } else {
      centerlineOfCurvature.start.z = -innerRadius
      centerlineOfCurvature.end.z = -innerRadius
    }

    let clocStart = new Vector3(centerlineOfCurvature.start.x, centerlineOfCurvature.start.y, centerlineOfCurvature.start.z)
    let clocEnd = new Vector3(centerlineOfCurvature.end.x, centerlineOfCurvature.end.y, centerlineOfCurvature.end.z)

    let clocAxis = clocEnd.sub(clocStart).normalize()
    if (bend.parentSide === 'right') clocAxis.multiplyScalar(-1)

    // Apply cylinder transform to each line
    for (let f = 0; f < bend.lineGroup.length; f++) {
      let line = bend.lineGroup[f]
      if (!line.stripes) throw new Error(`line in bend ${bend.id} has no stripes`)
      // Apply to each stripe
      for (let g = 0; g < line.stripes.length; g++) {

        // Compute rigid body matrix transform for going around the bend for this stripe. It's approximate because it
        // doesn't take into account stretching that takes place, but it's close enough for the collision model, which
        // has to use a rigid transform.
        let stripRigidBodyMat = new Matrix4()

        // If this is a fractional bend, the approximate matrix will either remain with the parent region, be
        // translated with the child region, or be part of the cylinder.
        let x = (g + 0.5) / line.stripes.length
        if (bend.parentSide === 'left') {
          x = 1 - x
        }
        if (x <= 0.5 - fractionBendTransform / 2) {
          // This stripe remains in the parent region
          // Do nothing
        } else if (x >= 0.5 + fractionBendTransform / 2) {
          // This stripe goes with the child region
          stripRigidBodyMat.premultiply(getBendMatrix(bend, arcified, fractionBendTransform * 100))
        } else {
          // Translate all stripes back to the start of the turn, placing each directly above/below the centerlineOfCurvature
          let translationVector = add(offsetTurnStart, mul(normal, -bend.allowance * (0.5 - x)))
          let transformedTranslationVector = new Vector3(translationVector.x, translationVector.y, 0)

          stripRigidBodyMat.premultiply(new Matrix4().makeTranslation(transformedTranslationVector.x, transformedTranslationVector.y, transformedTranslationVector.z))
          // Rotate stripe around the centerlineOfCurvature
          stripRigidBodyMat.premultiply(new Matrix4().makeTranslation(-clocStart.x, -clocStart.y, -clocStart.z))
          stripRigidBodyMat.premultiply(new Matrix4().makeRotationAxis(clocAxis, -bend.angle * (x - (0.5 - fractionBendTransform / 2)) * Math.PI / 180))
          stripRigidBodyMat.premultiply(new Matrix4().makeTranslation(clocStart.x, clocStart.y, clocStart.z))
        }

        // Apply all previous bend transforms
        stripRigidBodyMat.premultiply(mat)

        for (let h = 0; h < line.stripes[g].length; h++) {
          let stripe = line.stripes[g][h]
          if (!stripe.convexGroup) throw new Error(`Stripe ${h} in bend ${bend.id} has no convexGroup`)
          stripe.convexGroup.matrix = getMatrix4FromTHREEMatrix4(stripRigidBodyMat)

          let stripeMeshName = getBendStripeName(bend.id, f, g, h)
          let stripeMesh = model.getObjectByName(stripeMeshName)
          if (!stripeMesh) continue

          // Check to make sure stripeMesh is a mesh
          if (!(stripeMesh instanceof Mesh)) {
            throw new Error(`Object ${stripeMeshName} is not a mesh`)
          }

          let coords = stripeMesh.geometry.attributes.position.array
          let normals = stripeMesh.geometry.attributes.normal.array
          let originalCoords = stripe.originalCoords
          let originalNormals = stripe.originalNormals
          if (!originalCoords) throw new Error(`Stripe ${stripeMeshName} has no originalCoords`)
          if (!originalNormals) throw new Error(`Stripe ${stripeMeshName} has no originalNormals`)
          for (let i = 0; i < coords.length; i += 3) {
            vert.set(originalCoords[i], originalCoords[i + 1], originalCoords[i + 2])
            let cylVert = cylindricalTransformPoint(vert)
            coords[i] = cylVert.x
            coords[i + 1] = cylVert.y
            coords[i + 2] = cylVert.z

            vert2.set(originalNormals[i], originalNormals[i + 1], originalNormals[i + 2])
            let cylNorm = cylindricalTransformNormal(vert, vert2)
            normals[i] = cylNorm.x
            normals[i + 1] = cylNorm.y
            normals[i + 2] = cylNorm.z
          }

          stripeMesh.geometry.attributes.position.needsUpdate = true
          stripeMesh.geometry.attributes.normal.needsUpdate = true
          stripeMesh.geometry.computeBoundingBox()
          stripeMesh.geometry.computeBoundingSphere()

          stripeMesh.matrix.copy(mat)
        }
      }
    }

    // Apply to engrave paths on this bend
    let engravePathsThisBend = engravePaths.filter(ePath => ePath.parent === bend)
    for (let ePath of engravePathsThisBend) {

      let points = []
      for (let pt of ePath.poly) {
        // points.push(cylindricalTransformPoint(new Vector3(pt.x, pt.y, thickness + 0.001)))
        points.push(new Vector3(pt.x, pt.y, thickness + 0.001))
      }

      let engraveLine = model.getObjectByName(`engrave-${engravePaths.indexOf(ePath)}`)
      if (!engraveLine) continue
      if (!(engraveLine instanceof Line)) throw new Error(`Object ${engraveLine.name} is not a line`)

      let coords = engraveLine.geometry.attributes.position.array
      let originalCoords = ePath.originalCoords
      if (!originalCoords) throw new Error(`Engrave path ${engraveLine.name} has no originalCoords`)
      for (let i = 0; i < coords.length; i += 3) {
        let vert = new Vector3(originalCoords[i], originalCoords[i + 1], originalCoords[i + 2])

        let cylVert = cylindricalTransformPoint(vert)

        coords[i] = cylVert.x
        coords[i + 1] = cylVert.y
        coords[i + 2] = cylVert.z
      }

      engraveLine.geometry.attributes.position.needsUpdate = true
      engraveLine.geometry.computeBoundingBox()
      engraveLine.geometry.computeBoundingSphere()

      let mat = getBendStackMatrix(bend.bendStack, bendInfo.bends, arcified, percentBendTransform, percentBendTransformById)
      engraveLine.matrix.copy(mat)
    }
  }

  model.updateMatrixWorld()
}

/**
 * Return a transformation function that maps points within a bend allowance region onto a cylinder. If the fraction of
 * the bend transform is less than 1, points outside the fractioned bend allowance region will be mapped linearly just
 * as the neighboring regions are.
 * @param bend The bend to get the cylinder transform of
 * @param arcified The part
 * @param percentBendTransform 
 * @param percentBendTransformById 
 */
function getCylinderTransformFunction(bend: Bend, arcified: Arcified, percentBendTransform: number, percentBendTransformById?: Record<number, number> | null) {

  const { thickness, innerRadius, kFactor } = getPartMaterialBendProperties(arcified)

  if (bend.normal == null || bend.angle == null) {
    throw new Error("Bend normal or angle is null")
  }

  // Compute cylinder transform
  let fractionBendTransform = percentBendTransform / 100 * (percentBendTransformById?.[bend.id] ?? 100) / 100
  let allowance = Math.abs(bend.angle) * Math.PI / 180 * (innerRadius + kFactor * thickness)
  allowance *= fractionBendTransform
  // let allowanceDiff = originalAllowance - allowance

  let rightSide = {
    start: add(bend.lineGroup[0].start, mul(bend.normal, -allowance * 0.5)),
    end: add(bend.lineGroup[0].end, mul(bend.normal, -allowance * 0.5))
  }
  let leftSide = {
    start: add(bend.lineGroup[0].start, mul(bend.normal, allowance * 0.5)),
    end: add(bend.lineGroup[0].end, mul(bend.normal, allowance * 0.5))
  }
  let sideLineParent, sideLineChild
  if (bend.parentSide === 'left') {
    sideLineParent = { start: { ...leftSide.start }, end: { ...leftSide.end } }
    sideLineChild = { start: { ...rightSide.start }, end: { ...rightSide.end } }
  } else if (bend.parentSide === 'right') {
    sideLineParent = { start: { ...rightSide.start }, end: { ...rightSide.end } }
    sideLineChild = { start: { ...leftSide.start }, end: { ...leftSide.end } }
  } else {
    throw new Error('Not all bends were traversed')
  }

  let centerlineOfCurvature = { start: { ...sideLineParent.start, z: 0 }, end: { ...sideLineParent.end, z: 0 } }
  if (bend.angle > 0) {
    centerlineOfCurvature.start.z = innerRadius + thickness
    centerlineOfCurvature.end.z = innerRadius + thickness
  } else {
    centerlineOfCurvature.start.z = -innerRadius
    centerlineOfCurvature.end.z = -innerRadius
  }

  let cloc = centerlineOfCurvature
  let clocStart = new Vector3(cloc.start.x, cloc.start.y, cloc.start.z)
  let clocAxis = new Vector3(cloc.end.x, cloc.end.y, cloc.end.z)
    .sub(clocStart)
    .normalize()
  if (bend.parentSide === 'right') clocAxis.multiplyScalar(-1)


  // Copying same code that is used for the bendStack matrix
  // This is used in case we are doing a fractioned bend and need to extend the bend allowance region mesh linearly, using the same transform as the next region
  // slpStart is a vector from origin to start of sideline on parent side

  // let slcStartRotated = slpStart.clone() // Vector from origin to sideline parent
  let slcStartRotated = new Vector3(sideLineParent.start.x, sideLineParent.start.y, 0)
    .sub(clocStart) // Is now a vector from the centerline of curvature to the sideline parent
    .applyAxisAngle(clocAxis, -bend.angle * Math.PI / 180 * fractionBendTransform) // Is now a vector from the centerline of curvature to the point at which the child contour will start
    .add(clocStart) // Is now a vector from the origin to the point at which the child contour will start

  // Steps to rotate the child:
  // Translate slcStart to origin
  // Rotate about the axis (passing through origin)
  // Translate origin to slcStartRotated

  // Using the convenience functions part.translateOnAxis, part.rotateOnAxis, etc., was too darn confusing and ambiguous.
  let mat = new Matrix4()
  // When in doubt, just left-multiply.
  mat.premultiply(new Matrix4().makeTranslation(-sideLineChild.start.x, -sideLineChild.start.y, 0))
  mat.premultiply(new Matrix4().makeRotationAxis(clocAxis, -bend.angle * Math.PI / 180 * fractionBendTransform))
  mat.premultiply(new Matrix4().makeTranslation(slcStartRotated.x, slcStartRotated.y, slcStartRotated.z))

  // Create rotation matrix that will position cloc along the y-axis. This must always be a rotation about the z-axis.
  // Because clocAxis.z is always 0, we can use atan2 to get the angle.
  let rotationAngle = Math.PI / 2 - Math.atan2(clocAxis.y, clocAxis.x)
  let rotQuat = new Quaternion().setFromAxisAngle(new Vector3(0, 0, 1), rotationAngle)
  let iRotQuat = rotQuat.clone().invert()

  // Radius for cylindrical transform
  let r = clocStart.z

  // Radius for angle scaling
  let rAngleScaling = r
  if (r < 0) rAngleScaling -= thickness * kFactor
  if (r > 0) rAngleScaling -= thickness * (1 - kFactor)

  // Function to perform the cylindrical transformation
  const cylindricalTransformPoint = (vert: Vector3) => {
    const _vert = new Vector3().copy(vert)

    // Move to the origin
    _vert.x -= clocStart.x
    _vert.y -= clocStart.y

    // Apply rotation matrix to vert to put it in the x', y', z' coordinate system
    _vert.applyQuaternion(rotQuat)


    // If fractionBendTransform < 1, then some of the points in the bend allowance region mesh will not be inside the
    // fractioned bend allowance region. If that is the case, apply a different transform.
    if (_vert.x < 0) {
      // Return vert unchanged
      return vert
    } else if (_vert.x > allowance) {
      _vert.copy(vert)
      _vert.applyMatrix4(mat)
      return _vert
    } else {
      // Apply cylindrical transform to vert
      _vert.set(
        (r - _vert.z) * Math.sin(_vert.x / rAngleScaling),
        _vert.y,
        r - (r - _vert.z) * Math.cos(_vert.x / rAngleScaling)
      )
      // Reverse apply rotation matrix to put cylVert back into x, y, z, coordinate system
      _vert.applyQuaternion(iRotQuat)

      _vert.x += clocStart.x
      _vert.y += clocStart.y

      return _vert
    }

  }

  return cylindricalTransformPoint
}


/**
 * Return a transformation function that rotates normal vectors within a bend allowance region around a cylinder. If the fraction of
 * the bend transform is less than 1, points outside the fractioned bend allowance region will be mapped linearly just
 * as the neighboring regions are.
 * @param bend The bend to get the cylinder transform of
 * @param arcified The part
 * @param percentBendTransform 
 * @param percentBendTransformById 
 */
function getCylinderNormalTransformFunction(bend: Bend, arcified: Arcified, percentBendTransform: number, percentBendTransformById?: Record<number, number> | null) {

  const { thickness, innerRadius, kFactor } = getPartMaterialBendProperties(arcified)

  if (bend.normal == null || bend.angle == null) {
    throw new Error("Bend normal or angle is null")
  }

  // Compute cylinder transform
  let fractionBendTransform = percentBendTransform / 100 * (percentBendTransformById?.[bend.id] ?? 100) / 100
  let allowance = Math.abs(bend.angle) * Math.PI / 180 * (innerRadius + kFactor * thickness)
  allowance *= fractionBendTransform

  let rightSide = {
    start: add(bend.lineGroup[0].start, mul(bend.normal, -allowance * 0.5)),
    end: add(bend.lineGroup[0].end, mul(bend.normal, -allowance * 0.5))
  }
  let leftSide = {
    start: add(bend.lineGroup[0].start, mul(bend.normal, allowance * 0.5)),
    end: add(bend.lineGroup[0].end, mul(bend.normal, allowance * 0.5))
  }
  let sideLineParent
  if (bend.parentSide === 'left') {
    sideLineParent = { start: { ...leftSide.start }, end: { ...leftSide.end } }
  } else if (bend.parentSide === 'right') {
    sideLineParent = { start: { ...rightSide.start }, end: { ...rightSide.end } }
  } else {
    throw new Error('Not all bends were traversed')
  }

  let centerlineOfCurvature = { start: { ...sideLineParent.start, z: 0 }, end: { ...sideLineParent.end, z: 0 } }
  if (bend.angle > 0) {
    centerlineOfCurvature.start.z = innerRadius + thickness
    centerlineOfCurvature.end.z = innerRadius + thickness
  } else {
    centerlineOfCurvature.start.z = -innerRadius
    centerlineOfCurvature.end.z = -innerRadius
  }

  let cloc = centerlineOfCurvature
  let clocStart = new Vector3(cloc.start.x, cloc.start.y, cloc.start.z)
  let clocAxis = new Vector3(cloc.end.x, cloc.end.y, cloc.end.z)
    .sub(clocStart)
    .normalize()
  if (bend.parentSide === 'right') clocAxis.multiplyScalar(-1)

  // Steps to rotate the child:
  // Rotate about the axis (passing through origin)

  // Create rotation matrix that will position cloc along the y-axis. This must always be a rotation about the z-axis.
  // Because clocAxis.z is always 0, we can use atan2 to get the angle.
  let rotationAngle = Math.PI / 2 - Math.atan2(clocAxis.y, clocAxis.x)
  let rotQuat = new Quaternion().setFromAxisAngle(new Vector3(0, 0, 1), rotationAngle)

  // Function to perform the cylindrical transformation
  const cylindricalTransformNormal = (point: Vector3, normal: Vector3) => {
    const _point = new Vector3().copy(point)

    // Move point to the origin
    _point.x -= clocStart.x
    _point.y -= clocStart.y

    // Apply rotation matrix to vert to put it in the x', y', z' coordinate system
    _point.applyQuaternion(rotQuat)

    // If fractionBendTransform < 1, then some of the points in the bend allowance region mesh will not be inside the
    // fractioned bend allowance region. If that is the case, apply a different transform.
    let amount = Math.min(_point.x / allowance, 1)
    if (amount < 0) {
      // Return normal unchanged
      return normal
    } else {
      // Apply cylindrical transform to vert
      let mat = new Matrix4().makeRotationAxis(clocAxis, -bend.angle! * Math.PI / 180 * fractionBendTransform * amount)
      const _normal = new Vector3().copy(normal)
      _normal.applyMatrix4(mat)
      return _normal
    }

  }

  return cylindricalTransformNormal
}

/**
 * For a given bend stack, return a matrix that will transform a point from the flat pattern to the bent part. The
 * matrix is calculated by applying the transform matrix from each bend in the bend stack in reverse order.
 * @param bendStack The stack of bend ids to apply
 * @param bends The array of bends
 * @param arcified The arcified part
 * @param percentBendTransform The percentage of the bend transform to apply, as a value between 0 and 100.
 * @param percentBendTransformById A map of bend ids to percentages of bend transform to apply. If this is provided, it will be multiplied with the percentBendTransform parameter.
 * @returns A matrix that will transform a point from the flat pattern to the bent part.
 */
export function getBendStackMatrix(bendStack: number[], bends: Bend[], arcified: Arcified, percentBendTransform = 100, percentBendTransformById?: Record<number, number> | null) {

  let mat = new Matrix4() // Identity

  for (let i = bendStack.length - 1; i >= 0; i--) {
    let bend = bends.find(b => b.id === bendStack[i])
    if (!bend) throw new Error(`Bend with id ${bendStack[i]} not found`)
    let bendMat = getBendMatrix(bend, arcified, percentBendTransform * (percentBendTransformById?.[bend.id] ?? 100) / 100)
    mat.premultiply(bendMat)
  }

  return mat
}

/**
 * Gets the matrix that will transform a point from the flat pattern to the bent part across a single bend.
 * @param bend The bend
 * @param arcified The arcified part
 * @param percentBendTransform The percentage of the bend transform to apply, as a value between 0 and 100.
 */
function getBendMatrix(bend: Bend, arcified: Arcified, percentBendTransform = 100) {
  const { thickness, innerRadius, kFactor } = getPartMaterialBendProperties(arcified)
  if (bend.angle == null) throw new Error('Bend angle is null')
  if (!bend.normal) throw new Error('Bend has no normal property')

  let mat = new Matrix4() // Identity

  let fractionBendTransform = percentBendTransform / 100

  let allowance = Math.abs(bend.angle) * Math.PI / 180 * fractionBendTransform * (innerRadius + kFactor * thickness)
  let rightSide = {
    start: add(bend.lineGroup[0].start, mul(bend.normal, -allowance * 0.5)),
    end: add(bend.lineGroup[0].end, mul(bend.normal, -allowance * 0.5))
  }
  let leftSide = {
    start: add(bend.lineGroup[0].start, mul(bend.normal, allowance * 0.5)),
    end: add(bend.lineGroup[0].end, mul(bend.normal, allowance * 0.5))
  }
  let sideLineParent, sideLineChild
  if (bend.parentSide === 'left') {
    sideLineParent = { start: { ...leftSide.start }, end: { ...leftSide.end } }
    sideLineChild = { start: { ...rightSide.start }, end: { ...rightSide.end } }
  } else if (bend.parentSide === 'right') {
    sideLineParent = { start: { ...rightSide.start }, end: { ...rightSide.end } }
    sideLineChild = { start: { ...leftSide.start }, end: { ...leftSide.end } }
  } else {
    throw new Error('Not all bends were traversed')
  }

  let centerlineOfCurvature = { start: { ...sideLineParent.start, z: 0 }, end: { ...sideLineParent.end, z: 0 } }
  if (bend.angle > 0) {
    centerlineOfCurvature.start.z = innerRadius + thickness
    centerlineOfCurvature.end.z = innerRadius + thickness
  } else {
    centerlineOfCurvature.start.z = -innerRadius
    centerlineOfCurvature.end.z = -innerRadius
  }


  let cloc = centerlineOfCurvature
  let clocStart = new Vector3(cloc.start.x, cloc.start.y, cloc.start.z)
  let clocAxis = new Vector3(cloc.end.x, cloc.end.y, cloc.end.z)
    .sub(clocStart)
    .normalize()
  if (bend.parentSide === 'right') clocAxis.multiplyScalar(-1)

  // clocStart is vector from origin to start of centerline of curvature
  // clocAxis is a unit vector along centerline of curvature

  // Is it possible to do this transform without using bend.sideLineParent? So that we can easily adjust the bend angle without it affecting the translation?

  let slcStartRotated = new Vector3(sideLineParent.start.x, sideLineParent.start.y, 0)
    .sub(clocStart) // Is now a vector from the centerline of curvature to the sideline parent
    .applyAxisAngle(clocAxis, -bend.angle * Math.PI / 180 * fractionBendTransform) // Is now a vector from the centerline of curvature to the point at which the child contour will start
    .add(clocStart) // Is now a vector from the origin to the point at which the child contour will start

  // Steps to rotate the child:
  // Translate slcStart to origin
  // Rotate about the axis (passing through origin)
  // Translate origin to slcStartRotated

  // Using the convenience functions part.translateOnAxis, part.rotateOnAxis, etc., was too darn confusing and ambiguous.

  // When in doubt, just left-multiply.
  mat.premultiply(new Matrix4().makeTranslation(-sideLineChild.start.x, -sideLineChild.start.y, 0))
  mat.premultiply(new Matrix4().makeRotationAxis(clocAxis, -bend.angle * Math.PI / 180 * fractionBendTransform))
  mat.premultiply(new Matrix4().makeTranslation(slcStartRotated.x, slcStartRotated.y, slcStartRotated.z))

  return mat

}


/**
 * Transforms a Vector into a Clipper point.
 * @param pt The Vector to transform.
 * @returns The Clipper point.
 */
function toClipper(pt: Vector): ClipperPoint {
  return { X: pt.x, Y: pt.y }
}

/**
 * Transforms a Clipper point into a Vector.
 * @param pt The Clipper point to transform.
 * @returns The Vector.
 */
function fromClipper(pt: ClipperPoint): Vector {
  return { x: pt.X, y: pt.Y }
}

/**
 * Returns true if any value in arr1 equals that of arr2, according to a comparison function. Both arrays must be
 * sortable. If a comparison function is not provided, the arrays will be sorted by numeric value. This function is O(n
 * log n).
 * @param arr1 
 * @param arr2 
 * @param compare Comparison function to use to check for equivalence between items of arr1 and arr2.
 * @returns Whether any value in arr1 matches that of arr2.
 */
function matchArrayArray(arr1: number[], arr2: number[]): boolean
function matchArrayArray<T>(arr1: T[], arr2: T[], compare: (a: T, b: T) => number): boolean
function matchArrayArray<T>(arr1: T[], arr2: T[], compare?: (a: T, b: T) => number): boolean {

  // The overload signatures require that arr1 and arr2 are number array if compare is not provided, so
  // "as unknown as number" is okay.
  if (!compare) compare = (a, b) => (a as unknown as number) - (b as unknown as number)

  let arr1Clone = arr1.slice()
  let arr2Clone = arr2.slice()
  arr1Clone.sort(compare)
  arr2Clone.sort(compare)

  let cur1 = 0
  let cur2 = 0
  while (cur1 < arr1.length && cur2 < arr2.length) {
    let c = compare(arr1Clone[cur1], arr2Clone[cur2])
    if (c === 0) return true
    else if (c > 0) cur2++
    else if (c < 0) cur1++
  }
  return false
}

/**
 * Returns true if any value in arr1 strictly equals that of arr2. This function is O(n^2).
 * @param arr1 The first array.
 * @param arr2 The second array.
 * @returns Whether any value in arr1 matches that of arr2.
 * 
 */
function matchArrayArraySlow<T>(arr1: T[], arr2: T[]) {
  for (let a1 of arr1)
    for (let a2 of arr2)
      if (a1 === a2) return true
  return false
}

/**
 * Creates a wireframe box model.
 * @param bbox The bounding box to create a model for.
 * @param color The color of the box.
 */
export function createBboxModel(bbox: BoundingBox3, color = 0x777777) {
  let boxGeom = new BoxGeometry(
    bbox.max.x - bbox.min.x,
    bbox.max.y - bbox.min.y,
    bbox.max.z - bbox.min.z)
    .translate(
      (bbox.min.x + bbox.max.x) / 2,
      (bbox.min.y + bbox.max.y) / 2,
      (bbox.min.z + bbox.max.z) / 2)

  let boxMaterial = new MeshBasicMaterial({ color, wireframe: true })
  let box = new Mesh(boxGeom, boxMaterial)
  return box
}

/**
 * Modifies the geometry of an ExtrudeGeometry to group normals by whether they are facing the front, back, or side.
 * @param geometry The geometry to modify.
 */
function regroupExtrudeGeometry(geometry: ExtrudeGeometry) {

  let epsilon = 1e-9
  geometry.clearGroups()

  let groupIds = {
    FRONT: 0,
    BACK: 1,
    SIDE: 2
  }

  let currentGroup
  let currentGroupStart

  for (let i = 0; i < geometry.attributes.normal.count; i++) {
    let index = i * 3

    let norm = new Vector3(
      geometry.attributes.normal.array[index + 0],
      geometry.attributes.normal.array[index + 1],
      geometry.attributes.normal.array[index + 2],
    )

    let pos = new Vector3(
      geometry.attributes.position.array[index + 0],
      geometry.attributes.position.array[index + 1],
      geometry.attributes.position.array[index + 2],
    )

    norm.normalize()
    let isNormalVertical = Math.abs(norm.z) !== 0
    let isFront = pos.z > epsilon

    let group: 'FRONT' | 'BACK' | 'SIDE'
    if (isNormalVertical && isFront) {
      group = 'FRONT'
    } else if (isNormalVertical && !isFront) {
      group = 'BACK'
    } else if (!isNormalVertical) {
      group = 'SIDE'
    } else {
      throw new Error('This should never happen')
    }

    if (group !== currentGroup) {
      if (currentGroup && currentGroupStart != null) {
        geometry.addGroup(currentGroupStart, i - currentGroupStart, groupIds[currentGroup])
      }
      currentGroupStart = i
      currentGroup = group
    }
  }

  // Add final group
  if (currentGroupStart != null && currentGroup) {
    geometry.addGroup(currentGroupStart, geometry.attributes.normal.count - currentGroupStart, groupIds[currentGroup])
  }
}

export function createShape(poly: Vector[], holes: Vector[][] = [], minHoleSize = 0) {
  let shape = new Shape()
  if (poly.length === 0) {
    return shape
  }
  shape.moveTo(poly[0].x, poly[0].y)
  for (let i = 1; i < poly.length; i++) {
    shape.lineTo(poly[i].x, poly[i].y)
  }
  shape.closePath()

  if (holes.length > 0) {
    let holePaths = []
    for (let hole of holes) {
      let holeBbox = { min: hole[0], max: hole[0] }
      let holePath = new Path()
      holePath.moveTo(hole[0].x, hole[0].y)
      for (let i = 1; i < hole.length; i++) {
        holePath.lineTo(hole[i].x, hole[i].y)
        holeBbox.min = min(holeBbox.min, hole[i])
        holeBbox.max = max(holeBbox.max, hole[i])
      }
      if (holeBbox.max.x - holeBbox.min.x < minHoleSize && holeBbox.max.y - holeBbox.min.y < minHoleSize) {
        continue
      }
      holePath.closePath()
      holePaths.push(holePath)
    }
    shape.holes = holePaths
  }

  return shape
}

function buildPath(path: Path, contour: Contour) {
  let start = getPointTangentOfEntity(contour.entities[0], contour.entities[0].reversed ? 1 : 0)[0]
  path.moveTo(start.x, start.y)
  for (let entity of contour.entities) {
    switch (entity.type) {
      case 'LINE':
        let end = getPointTangentOfEntity(entity, entity.reversed ? 0 : 1)[0]
        path.lineTo(end.x, end.y)
        break
      case 'ARC':
        path.absarc(entity.x, entity.y, entity.r, entity.startAngle, entity.endAngle, entity.reversed)
        break
      case 'CIRCLE':
        path.absarc(entity.x, entity.y, entity.r, 0, 2 * Math.PI, entity.reversed)
        break
    }
  }
  path.closePath()
}

function createShapeFromContours(contour: Contour, holes: Contour[], minHoleSize = 0) {
  let shape = new Shape()
  if (contour.entities.length === 0) {
    return shape
  }
  buildPath(shape, contour)
  if (holes.length > 0) {
    let holePaths = []
    for (let hole of holes) {
      if (hole.bbox.max.x - hole.bbox.min.x < minHoleSize && hole.bbox.max.y - hole.bbox.min.y < minHoleSize) {
        continue
      }
      let holePath = new Path()
      buildPath(holePath, hole)
      holePaths.push(holePath)
    }
    shape.holes = holePaths
  }

  return shape
}

export function createLineSegmentGroupFromConvexGroup(convexGroup: ConvexGroup) {
  let material = new LineBasicMaterial({
    color: 0x00ff00,
    linewidth: 2,
  })
  let group = new Group()
  for (let mesh of convexGroup.meshes) {
    for (let edge of mesh.edges) {
      let points = [
        new Vector3(mesh.verts[edge.v[0]].p.x, mesh.verts[edge.v[0]].p.y, mesh.verts[edge.v[0]].p.z),
        new Vector3(mesh.verts[edge.v[1]].p.x, mesh.verts[edge.v[1]].p.y, mesh.verts[edge.v[1]].p.z),
      ]

      // Warning: this geometry is not disposed when the group is removed from the scene
      let geometry = new BufferGeometry().setFromPoints(points)

      let lineObj = new Line(geometry, material)
      group.add(lineObj)
    }
  }
  group.matrixAutoUpdate = false
  group.matrix = new Matrix4()
  if (convexGroup.matrix) assignTHREEMatrix4FromMatrix4(group.matrix, convexGroup.matrix)
  group.updateMatrixWorld()
  return group
}

export function getRegionName(regionId: number, regionPolyIndex: number) {
  return `region-${regionId}.${regionPolyIndex}`
}

export function getBendStripeName(bendId: number, lineGroupIndex: number, stripeIndex: number, stripePolyIndex: number) {
  return `bend-${bendId}.${lineGroupIndex}.${stripeIndex}.${stripePolyIndex}`
}

export function parseRegionName(regionName: string) {
  const regionRegex = /^region-([0-9]+)\.([0-9]+)$/
  let match = regionName.match(regionRegex)
  if (match) {
    return {
      regionId: parseInt(match[1]),
      regionPolyIndex: parseInt(match[2])
    }
  }
  return null
}

export function parseBendStripeName(bendStripeName: string) {
  const bendRegex = /^bend-([0-9]+)\.([0-9]+)\.([0-9]+)\.([0-9]+)$/
  let match = bendStripeName.match(bendRegex)
  if (match) {
    return {
      bendId: parseInt(match[1]),
      lineGroupIndex: parseInt(match[2]),
      stripeIndex: parseInt(match[3]),
      stripePolyIndex: parseInt(match[4])
    }
  }
  return null
}
